import { spawn, spawnSync } from "node:child_process";
import { existsSync, readFileSync } from "node:fs";
import { createRequire } from "node:module";
import type {
  CanUseTool,
  PermissionResult,
  SDKAssistantMessage,
  SDKMessage,
  SDKUserMessage,
  Options as SdkOptions,
  Query as SdkQuery,
  query as sdkQueryFn,
} from "@anthropic-ai/claude-agent-sdk";
import type { Logger, ModelTokenUsage, TokenUsage } from "@skaile/workspaces/types";
// Static namespace import so bun --compile bundles zod into the binary.
// Dynamic import("zod") via a string variable evades bun's static analysis
// and fails at runtime inside /$bunfs/. The unwrap (zNS.z ?? zNS.default
// ?? zNS) handles both bun's native ESM and vitest's CJS-interop resolver,
// which return the zod namespace at different keys.
import * as zNS from "zod";

const zStatic: any = (zNS as any).z ?? (zNS as any).default ?? zNS;

import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import { dispatchCapability } from "../capability-dispatch.js";
import { buildClaudeCloudEnv, type CloudId, SUPPORTED_CLOUDS } from "../cloud.js";
import {
  AccountUnavailableError,
  AuthError,
  classifyClaudeSdkError,
  reclassifyAuthAsAccountUnavailable,
} from "../error-classifier.js";
import { getBridgeLogger } from "../logger.js";
import { fetchProviderModels, type ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import {
  type AgentConfig,
  AgentDriver,
  type AgentEvent,
  type AgentError,
  type AgentMessage,
  type ContentBlock,
  type DriverInfo,
  type LimitFailoverResult,
  type ProviderResponseObservation,
  type SlashCommandInfo,
  type ToolCall,
} from "../types.js";
import { resolveClaudeConfigDir } from "./resume-probe.js";
import { sanitizeAssistantText, streamingSafeSanitize } from "./sanitize-assistant-text.js";
import { scrubPoisonedTranscript } from "./scrub-transcript.js";

/**
 * Best-effort conversion of a JSON Schema object's top-level `properties` into
 * a `Record<string, ZodTypeAny>` accepted by `createSdkMcpServer`'s
 * `inputSchema`. The SDK only needs a shallow shape to advertise to the LLM —
 * deep validation is handled by the registry's Zod schema in `hooks.invoke()`.
 *
 * Returns null on schemas the bridge can't represent (anyOf at the root, etc.);
 * the caller falls back to a permissive `{ args: z.unknown().optional() }`.
 */
function jsonSchemaToZodLoose(
  schema: Record<string, unknown>,
  z: any,
): Record<string, unknown> | null {
  const props = (schema?.properties ?? null) as Record<string, any> | null;
  if (!props || typeof props !== "object") return null;
  const required = new Set(Array.isArray(schema?.required) ? (schema.required as string[]) : []);
  const out: Record<string, unknown> = {};
  for (const [key, def] of Object.entries(props)) {
    let zod: any;
    switch (def?.type) {
      case "string":
        zod = z.string();
        break;
      case "number":
      case "integer":
        zod = z.number();
        break;
      case "boolean":
        zod = z.boolean();
        break;
      case "array":
        zod = z.array(z.any());
        break;
      case "object":
        zod = z.record(z.string(), z.any());
        break;
      default:
        zod = z.any();
    }
    if (def?.description && typeof def.description === "string") {
      zod = zod.describe(def.description);
    }
    if (!required.has(key)) zod = zod.optional();
    out[key] = zod;
  }
  return out;
}

// The Claude Agent SDK bundles ajv which uses CJS require() internally.
// Polyfill globalThis.require so it works in a pure ESM context.
// In CJS (e.g. NestJS webpack bundle), require already exists — skip.
if (typeof globalThis.require === "undefined") {
  try {
    // Dynamic eval avoids static parse errors in CJS bundlers
    // biome-ignore lint/security/noGlobalEval: intentional — CJS/ESM interop requires dynamic eval
    const metaUrl = (0, eval)("import.meta.url");
    (globalThis as any).require = createRequire(metaUrl);
  } catch {
    // CJS context — require is already available via the module system
  }
}

/**
 * Agent driver for the Anthropic Claude Agent SDK.
 *
 * Runs the agent in-process — no subprocess is spawned. Anthropic models only.
 *
 * @remarks
 * **Lazy-loading** — `@anthropic-ai/claude-agent-sdk` is imported dynamically inside
 * `start()` so it remains an optional peer dependency. If the package is not installed
 * an actionable error is thrown. The CJS/ESM `require` polyfill at the top of this file
 * is required because the SDK bundles ajv which uses `require()` internally.
 *
 * **Session continuity (streaming input mode)** — the query is constructed with a
 * persistent pushable `AsyncIterable` prompt, never a plain string. A string prompt
 * makes the SDK set `isSingleUserTurn` and close the CLI's stdin at the first
 * `result`; a CLI kept alive past that close (live `run_in_background` tasks) then
 * silently drops every later write — the 30-minute deaf-query wedge of
 * skaile-ai/workspaces#566. With the iterable prompt, stdin stays open for the
 * driver's whole query lifetime: each `prompt()` pushes one user message into the
 * live stream, and the stream is ended only at teardown. When no live query exists
 * a new one is started, applying a three-level resume chain:
 *   1. `resumeSessionId` from config (explicit caller resume)
 *   2. `sessionId` captured from the current driver lifetime
 *   3. `continue: true` (resume most recent session in cwd from disk)
 *
 * **Turn settling** — a turn may only be settled by the query that actually ran
 * it. Streaming input mode lets a `prompt()` land on a resident query whose
 * generator is already ending (any listener that prompts from inside the
 * driver's own `result` emit does exactly that), and the ending consumer loop
 * would then settle the *new* turn on its way out — resolving instantly with
 * zero output, which is how threshold compaction persisted empty summaries.
 * Hence: a pending turn that has not seen a single SDK message when its serving
 * generator ends is rejected as retryable, never resolved.
 *
 * **Tool synthesis** — emits `tool_call` and `tool_execution_end` events that the
 * runner's activity tracker needs for file-change detection. These are not native SDK
 * events; they are synthesised from `assistant` and `user` SDK messages.
 *
 * **agentName** — maps to the `agent` SDK query option. The SDK reads
 * `.claude/agents/<agentName>.md` from `cwd` for model, tools, and system prompt.
 * Requires the agent file to have been rendered by `asset-manager` (claudeCodeRenderer)
 * during `skaile install`. Ignored by other drivers.
 *
 * **mcpServers** — `AgentConfig.mcpServers` passes in-process MCP server instances to
 * the SDK's `mcpServers` query option. Used by `workspaces/connectors` to inject
 * resource tools as native `tool_use`. Ignored by other drivers.
 *
 * @docLink packages/bridge/drivers#claude-sdk-driver
 */

/**
 * One-shot recovery budgets for a single logical {@link ClaudeSdkDriver.prompt}
 * turn. Each recovery path owns an independent budget so an earlier recovery of
 * one kind never starves a later recovery of a different kind — most
 * importantly, a stale-resume retry must not consume the auth self-heal budget,
 * because the fresh session it starts can still hit an expired credential.
 * Total retries stay bounded (at most one per kind).
 */
interface RetryBudget {
  /** A stale-resume ("No conversation found") retry has been spent. */
  staleResume: boolean;
  /** A poisoned-transcript scrub retry has been spent. */
  poison: boolean;
  /** An auth self-heal (`onAuthError`) retry has been spent. */
  auth: boolean;
  /** A "the serving query ended before this turn ran" retry has been spent. */
  queryEnded: boolean;
  /** A seat failover (`onLimitBlocked`) retry has been spent. */
  limit: boolean;
}

/** A pristine budget for the first attempt of a turn (nothing spent yet). */
function freshRetryBudget(): RetryBudget {
  return { staleResume: false, poison: false, auth: false, queryEnded: false, limit: false };
}

/**
 * Whether a classified error means **this seat is out of capacity** — the only
 * thing a seat failover can fix, and the only thing worth counting against the
 * seat's health.
 *
 * The `rate_limit` category is broader than that. Two of its members are not
 * seat problems at all, and treating them as such would burn a mint round trip
 * on every occurrence and skew the platform's per-seat 429 counters:
 *   - `error_max_budget_usd` — OUR per-turn spend cap stopped the agent. The
 *     provider never rejected anything.
 *   - `overloaded_error` (529) — Anthropic is saturated. Every peer seat is
 *     behind the same capacity, so switching moves nothing.
 */
function isSeatCapacityFailure(detail: AgentError, subtype?: string): boolean {
  if (detail.category !== "rate_limit") return false;
  if (subtype === "error_max_budget_usd") return false;
  return detail.statusCode !== 529;
}

/**
 * Control-flow marker: the turn was enqueued into a query whose generator
 * ended before the turn saw a single SDK message, so the model never ran it.
 *
 * This is not a user-visible failure — {@link ClaudeSdkDriver.runPrompt}
 * catches it and replays the prompt on a fresh query. It exists as a distinct
 * type purely so that catch can tell "nobody ran this turn" apart from every
 * real error, which must never be silently retried.
 */
class QueryEndedBeforeTurnError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QueryEndedBeforeTurnError";
  }
}

/**
 * Inactivity budget for a single turn: how long the driver waits for *any* SDK
 * message before declaring the query wedged.
 *
 * 30 minutes is a ceiling, not a target. A long-running `Bash` tool call
 * produces no SDK messages at all, so a tighter default would false-trip on
 * real work; tune per-deployment via `SKAILE_TURN_STALL_MS`.
 */
const DEFAULT_TURN_STALL_MS = 30 * 60_000;

/**
 * Resolved per call, never at module load: tests (and the runner's env
 * plumbing) set `SKAILE_TURN_STALL_MS` after this module is imported.
 */
function turnStallMs(): number {
  const raw = Number(process.env.SKAILE_TURN_STALL_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_TURN_STALL_MS;
}

/**
 * Tighter inactivity budget that applies until the FIRST SDK message of a turn
 * arrives. A dispatched prompt normally yields something within seconds — an
 * init message, a stream delta, an API-retry notice, or an error result (even
 * an upstream rejection returns in under a second). Total silence from dispatch
 * onward means the query is deaf, not busy, so recovery should not wait out the
 * full mid-turn budget (which exists for long silent tool calls). Sized
 * generously above worst-case time-to-first-token on a huge resumed context.
 */
const DEFAULT_FIRST_MESSAGE_STALL_MS = 5 * 60_000;

/**
 * Resolved per call, like {@link turnStallMs}. Clamped to the mid-turn budget:
 * the first-message tier is a tightening, never a loosening — a deployment
 * that shrinks `SKAILE_TURN_STALL_MS` below the first-message default keeps
 * the smaller value for both tiers.
 */
function firstMessageStallMs(): number {
  const raw = Number(process.env.SKAILE_FIRST_MESSAGE_STALL_MS);
  const value = Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_FIRST_MESSAGE_STALL_MS;
  return Math.min(value, turnStallMs());
}

/**
 * Fragments only the Claude CLI's own auth renders carry. Used to tell an
 * upstream 401 apart from a model answer that merely discusses authentication,
 * which the classifier's keyword bucket cannot do on its own.
 */
const CLI_AUTH_RENDER_MARKERS = [
  "failed to authenticate",
  "api error:",
  "authentication_error",
  "not logged in",
  "please run /login",
  "oauth authentication is currently not supported",
];

function isCliAuthRenderText(text: string): boolean {
  const lower = text.toLowerCase();
  return CLI_AUTH_RENDER_MARKERS.some((marker) => lower.includes(marker));
}

/**
 * Grace period for the follow-up iterator throw that an `is_error` result with
 * an empty `errors[]` promises but does not guarantee.
 *
 * Waiting for that throw is what preserves the richer classification (and with
 * it the 401 self-heal), so this budget is sized well above the ~200 ms rescue
 * latency documented in note 2026-06-08-claude-sdk-error-result-handling rather
 * than tight: pre-empting a throw that was about to land would downgrade a
 * recoverable `AuthError` into a bare retry. Five seconds is imperceptible next
 * to the 30-minute stall watchdog that used to be the only exit from this shape.
 */
const DEFAULT_ERROR_RESULT_GRACE_MS = 5_000;

/** Resolved per call, like {@link turnStallMs} — tests set the env after import. */
function errorResultGraceMs(): number {
  const raw = Number(process.env.SKAILE_ERROR_RESULT_GRACE_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_ERROR_RESULT_GRACE_MS;
}

/**
 * `process.env` with the `undefined` entries dropped — the shape the SDK child
 * env and {@link buildClaudeCloudEnv} expect.
 */
function snapshotProcessEnv(): Record<string, string> {
  const env: Record<string, string> = {};
  for (const [k, v] of Object.entries(process.env)) {
    if (v !== undefined) env[k] = v;
  }
  return env;
}

/**
 * Fraction of `maxTurns` at which the resident query is recycled at the next
 * turn boundary — see {@link ClaudeSdkDriver.shouldRecycleQuery}.
 */
const RECYCLE_TURNS_FRACTION = 0.8;

/**
 * Absolute agentic-turn headroom floor for the recycle guard: recycle when
 * fewer than this many turns remain, regardless of the fraction. Keeps small
 * `maxTurns` budgets (default 15) from entering a turn nearly exhausted.
 */
const MIN_RECYCLE_HEADROOM = 5;

/**
 * The persistent prompt stream backing one SDK query (streaming input mode).
 * The SDK's `streamInput` loop iterates this for the query's whole lifetime and
 * closes the CLI's stdin only when the iterable ends — which {@link end} does
 * at driver-controlled teardown, never at a turn boundary.
 */
class PushableInputStream implements AsyncIterable<SDKUserMessage> {
  private queue: SDKUserMessage[] = [];
  private wake: (() => void) | null = null;
  private done = false;
  private consumed = false;

  /** `true` once {@link end} ran — pushes are no longer possible. */
  get ended(): boolean {
    return this.done;
  }

  push(message: SDKUserMessage): void {
    if (this.done) throw new Error("input stream already ended");
    this.queue.push(message);
    this.wake?.();
  }

  /** Finish the iterable; the SDK responds by closing the CLI's stdin. Idempotent. */
  end(): void {
    if (this.done) return;
    this.done = true;
    this.wake?.();
  }

  async *[Symbol.asyncIterator](): AsyncIterator<SDKUserMessage> {
    // Single-consumer contract: a second iterator would interleave shift()s
    // and clobber `wake`, silently losing messages.
    if (this.consumed) throw new Error("input stream already has a consumer");
    this.consumed = true;
    while (true) {
      while (this.queue.length > 0) {
        yield this.queue.shift()!;
      }
      if (this.done) return;
      await new Promise<void>((resolve) => {
        this.wake = resolve;
      });
      this.wake = null;
    }
  }
}

/** How long `abort()` waits for the consumer loop to unwind on its own. */
const ABORT_GRACE_MS = 3_000;

/** One entry of the SDK result's `modelUsage` map (`ModelUsage` in the SDK's `sdk.d.ts`). */
type SdkModelUsage = {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadInputTokens?: number;
  cacheCreationInputTokens?: number;
  costUSD?: number;
  contextWindow?: number;
};

const MODEL_USAGE_COUNTERS: ReadonlyArray<
  [keyof Omit<ModelTokenUsage, "contextWindow">, keyof SdkModelUsage]
> = [
  ["inputTokens", "inputTokens"],
  ["outputTokens", "outputTokens"],
  ["cacheReadTokens", "cacheReadInputTokens"],
  ["cacheCreationTokens", "cacheCreationInputTokens"],
  ["costUsd", "costUSD"],
];

/**
 * Turn the SDK's run-cumulative `modelUsage` into this turn's per-model share.
 *
 * Each counter is the difference against `previous` (the map from the last
 * result of the same SDK run); `previous === null` means the run just started,
 * so the whole map is the turn. A model whose counter went backwards is
 * re-baselined the way the cost delta treats a decrease in `total_cost_usd`:
 * its current values are the turn. Models whose counters did not move are
 * dropped, and `contextWindow` is carried as-is since it is a property, not a
 * counter. Returns `null` when no model moved.
 */
export function diffModelUsage(
  current: Record<string, SdkModelUsage>,
  previous: Record<string, SdkModelUsage> | null,
): Record<string, ModelTokenUsage> | null {
  const out: Record<string, ModelTokenUsage> = {};
  for (const [model, usage] of Object.entries(current)) {
    let prev = previous?.[model];
    if (
      prev &&
      MODEL_USAGE_COUNTERS.some(([, sdkKey]) => {
        const cur = usage[sdkKey];
        const before = prev?.[sdkKey];
        return typeof cur === "number" && typeof before === "number" && cur < before;
      })
    ) {
      prev = undefined;
    }
    const entry: ModelTokenUsage = {};
    let moved = false;
    for (const [outKey, sdkKey] of MODEL_USAGE_COUNTERS) {
      const cur = usage[sdkKey];
      if (typeof cur !== "number") continue;
      const before = prev?.[sdkKey];
      const delta = cur - (typeof before === "number" ? before : 0);
      entry[outKey] = delta;
      if (delta > 0) moved = true;
    }
    if (!moved) continue;
    if (typeof usage.contextWindow === "number") entry.contextWindow = usage.contextWindow;
    out[model] = entry;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * The `deny` message the SDK records as the tool_result when the user types in
 * the composer instead of answering an open `AskUserQuestion` dialog.
 *
 * This string is the model's ONLY signal that the question died — it never sees
 * the dialog, the click that did not happen, or any explanation we do not put
 * here. A bare reason ("superseded by a new prompt") reads as an unexplained
 * cancellation: on 2026-08-11 the model wrote "The user responded before my
 * question could be answered. Let me see what they said.", found nothing to
 * look at, and ended the turn on thinking alone, so the user's message went
 * unanswered. The pointer to the incoming user turn is therefore the payload,
 * not decoration.
 *
 * It deliberately does NOT carry the user's text: the composer message is
 * delivered once, as the next user turn, through the normal prompt path.
 */
const QUESTION_SUPERSEDED_BY_PROMPT =
  "The user replied in chat instead of answering this dialog, so the question was cancelled. " +
  "Their reply arrives as the next user turn — read it before you re-ask or assume an answer.";

// Memoized `findClaudeBinary()` result. Process-wide: the resolved path doesn't
// change at runtime, so the `which claude` spawn happens at most once. A separate
// `resolved` flag distinguishes "looked up, found nothing" from "not yet looked up".
let claudeBinaryCache: string | undefined;
let claudeBinaryResolved = false;

export class ClaudeSdkDriver extends AgentDriver {
  readonly driverInfo: DriverInfo = {
    id: "claude-sdk",
    name: "Claude Agent SDK",
    modelAgnostic: false,
    supportsInBandAbort: true,
  };

  private readonly log: Logger = getBridgeLogger("claude-sdk");
  private config: AgentConfig;
  private abortController: AbortController | null = null;
  private running = false;
  private sdk: { query: typeof sdkQueryFn } | null = null;
  private toolIdToName = new Map<string, string>();
  private prevText = "";
  /**
   * Count of sanitized characters already streamed for the current message.
   * Lets {@link handleStreamEvent} emit only the newly-stable sanitized suffix
   * as `_textDelta`, so a leaked marker that spans deltas is never half-sent.
   */
  private emittedSanitizedLen = 0;
  /** Set once the streaming text of the current message classifies as a self-healable auth render. */
  private suppressingAuthStream = false;
  /**
   * The auth render this attempt dropped on the promise that a self-heal would
   * retry — the evidence {@link handleResultErrorPayload} otherwise lacks when
   * the SDK reports the same 401 as an `is_error` result with an empty
   * `errors[]`. Written only through {@link stashSuppressedAuthRender}, which
   * applies a stricter test than suppression does; see its docblock. Cleared at
   * both ends of {@link runPrompt}, so it can only ever describe the turn that
   * is failing.
   */
  private suppressedAuthRenderText: string | null = null;

  /** Active query — stays alive between turns (streaming input mode). */
  private query: SdkQuery | null = null;
  /**
   * The live query's prompt stream. Created in {@link startQuery}, fed one user
   * message per turn, and ended wherever the query is dropped — an ended stream
   * with a live query means the CLI's stdin is closed and the query can never
   * accept another turn.
   */
  private inputStream: PushableInputStream | null = null;
  /**
   * `true` once any SDK message arrived after the current turn's watchdog was
   * armed — flips the stall budget from the first-message tier to the mid-turn
   * ceiling. See {@link armTurnStallWatchdog}.
   */
  private firstSdkMessageSeen = false;
  /** When the live query was spawned — the origin for the first-message timing. */
  private queryStartedAt = 0;
  /**
   * One-shot: armed at each spawn, disarmed by the first message of THAT spawn.
   * Deliberately not `firstSdkMessageSeen`, which the stall watchdog resets every
   * turn — a resident query serves turns 2..N without re-spawning, so reusing it
   * would measure from the original spawn and report the idle time between turns
   * as SDK latency.
   */
  private awaitingFirstMessageOfSpawn = false;
  /** What the spawn actually resumed, captured before a fallback can clear it. */
  private spawnResumed = false;
  /**
   * `num_turns` reported by the most recent result message. Feeds
   * {@link shouldRecycleQuery}; reset on every fresh spawn.
   */
  private lastNumTurns: number | null = null;
  /**
   * OS pid of the CLI subprocess backing `query`, captured via
   * `spawnClaudeCodeProcess` in {@link startQuery}. Feeds
   * {@link snapshotDescendantPids}; see `_devlog/notes/2026-08-12-abort-kills-tool-subprocess.md`.
   */
  private cliProcessPid: number | null = null;
  /** Resolves when the current turn completes. */
  private turnResolve: (() => void) | null = null;
  /** Rejects when the current turn errors. */
  private turnReject: ((err: Error) => void) | null = null;
  /**
   * Settled tail for complete logical prompts. Rejections are swallowed only on
   * the tail so one failed turn cannot wedge later callers.
   */
  private promptTail: Promise<void> = Promise.resolve();
  /** Guards against duplicate agent_end emissions per turn. */
  private turnCompleted = false;
  /** `Date.now()` of the most recent SDK message — the watchdog's liveness signal. */
  private lastSdkMessageAt = 0;
  /** Armed while a turn is in flight; trips when the SDK goes silent for too long. */
  private turnStallTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Armed only while an `is_error` result waits for its follow-up iterator
   * throw; fails the turn if that throw never lands. Cleared at every turn
   * start and settle site so it can never fire into a later turn.
   */
  private errorResultGraceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Mirror of the in-flight {@link prompt} call's auth retry budget: `false`
   * while the `onAuthError` self-heal is still available, `true` once it has
   * been spent. Read by {@link failTurn} to decide whether to defer the
   * `agent-event: error` emission for a self-healable auth error.
   *
   * Set unconditionally at the top of every {@link runPrompt} attempt; never
   * reset elsewhere — the next call's set is the only legitimate transition.
   */
  private authSelfHealUsed = false;
  /**
   * Mirror of the in-flight turn's seat-failover budget — same contract as
   * {@link authSelfHealUsed}, for the `onLimitBlocked` path.
   */
  private limitFailoverUsed = false;
  /**
   * Set when THIS attempt saw an upstream usage-limit rejection: either the
   * SDK's `rate_limit_event` telemetry with `status: "rejected"`, or an error
   * result the classifier calls `rate_limit`.
   *
   * The telemetry is the load-bearing half. A subscription ceiling arrives as
   * `{"status":"rejected","type":"five_hour"}` followed by an error result
   * whose text ("You've hit your session limit · resets 9:50am") matches none
   * of the classifier's rate-limit keywords — so without this flag the
   * rejection classifies as `unknown` and no failover is reachable at all.
   *
   * Reset at the top of every {@link runPrompt} attempt, so the replay after a
   * failover observes its own outcome rather than inheriting the first one's.
   */
  private limitRejectionSeen = false;
  /**
   * True when {@link handleResultErrorPayload} rejected this turn ONLY so the
   * seat-failover branch could run — a turn that, before this existed, would
   * have emitted its error and resolved. If the failover then declines, the
   * rejection is undone so a limit block with no peer available looks exactly
   * as it always has downstream (one error event, one `agent_end`, a resolved
   * promise) rather than also drawing the runner's fatal "prompt failed".
   */
  private limitTurnRejectedForFailover = false;
  /**
   * Whether this attempt already reported a provider rejection. One upstream
   * failure routinely reaches the driver twice — `rejected` telemetry followed
   * by the error result, or an `is_error` result followed by the iterator throw
   * carrying the real text — and these observations feed counters, so counting
   * one rejection twice would overstate the seat's pressure.
   */
  private providerResponseReported = false;
  /** Limit-window metadata from this attempt's rejection telemetry, if any. */
  private lastLimitInfo: {
    limitType?: string;
    resetsAt?: number;
    utilization?: number;
  } | null = null;
  /**
   * The `agent-event: error` {@link failTurn} withheld because a recovery
   * (auth self-heal or seat failover) was about to run. The recovery emits it
   * if it declines, and drops it if it retries — so a recovered turn shows the
   * user nothing and a failed one shows exactly one error.
   */
  private deferredTurnError: AgentEvent | null = null;
  /** Tracks whether a session has been started (for continue: true). */
  private hasSession = false;
  /** Session ID from the SDK — used for streamInput messages. */
  private sessionId = "";
  /**
   * Set while the SDK is paused inside `canUseTool` on an `AskUserQuestion`
   * call, waiting for the user's answer. The SDK delivers every sub-question in
   * one call (one `toolUseID`) and the turn stays paused until we resolve with
   * a {@link PermissionResult}. We accumulate `answers[questionText] = reply` —
   * the same keying the SDK expects back — and resolve `allow` once every entry
   * in `questionTexts` has one. On abort/hibernate the request is denied so the
   * SDK writes a clean tool_result (no dangling tool_use to poison the resumed
   * transcript). Null when no question is pending.
   */
  private pendingQuestion: {
    resolve: (result: PermissionResult) => void;
    originalInput: Record<string, unknown>;
    questionTexts: string[];
    answers: Record<string, string>;
  } | null = null;
  /** Cached slash commands discovered from the SDK. */
  private cachedCommands: SlashCommandInfo[] = [];
  /** Token usage from the most recent completed turn. */
  private _lastTokens: TokenUsage | null = null;
  /**
   * Prompt size of the most recent API request — the real context fill. Kept
   * apart from `_lastTokens`, whose result-message usage is cumulative over
   * every API call in the turn and so cannot answer "how full is the context".
   */
  private _lastContextTokens: number | null = null;
  /** Context window size from the most recent result's modelUsage. */
  private _contextWindow: number | null = null;
  /**
   * Model id of the last main-thread assistant message. `modelUsage` also
   * holds subagent and housekeeping models, so this keys the main window.
   */
  private _lastMainModel: string | null = null;
  /** Per-model usage of the most recent completed turn, derived in `captureResultUsage`. */
  private _lastModelUsage: Record<string, ModelTokenUsage> | null = null;
  /**
   * The raw `modelUsage` map from the most recently seen result message. Like
   * `total_cost_usd` it is cumulative over the whole SDK run, so a turn's share
   * is the per-model difference against this snapshot.
   */
  private _lastCumulativeModelUsage: Record<string, SdkModelUsage> | null = null;
  /**
   * The SDK's `total_cost_usd` from the most recently seen result message —
   * cumulative over the whole SDK run/session, not per-turn. Diffed against
   * the next result's total to derive the per-turn delta.
   */
  private _lastCumulativeCostUsd = 0;
  /** Per-turn cost delta derived from `total_cost_usd`, emitted as the `result` event's `costUsd`. */
  private _lastTurnCostUsd = 0;
  /** Whether the last result carried `total_cost_usd`; 0 alone cannot tell a free turn from an unreported one. */
  private _lastCostReported = false;
  /**
   * Bumped by every `startQuery()` spawn. Lets `captureResultUsage` detect a
   * fresh CLI process deterministically, instead of only inferring it from
   * `total_cost_usd` happening to decrease.
   */
  private _querySpawnGeneration = 0;
  /** Spawn generation `_lastCumulativeCostUsd` was captured under. */
  private _costTrackingGeneration = -1;
  /**
   * `true` when the active credential is a Claude Code OAuth subscription
   * (no API key in config) rather than a raw `ANTHROPIC_API_KEY`. Set on
   * every `startQuery` because the credential source can be reconfigured
   * at runtime via `configure`. Threaded into `classifyClaudeSdkError` so
   * upstream auth-shaped failures get a quota-aware hint when warranted.
   */
  private usingOauthCredential = false;

  /**
   * @param config - Driver configuration. Claude SDK-relevant fields:
   *   - `cwd` — working directory; SDK session files and agent definitions are read from here.
   *   - `model` — Anthropic model identifier (e.g. `"claude-sonnet-4-5"`). Defaults to `"sonnet"`.
   *   - `apiKeys.anthropic` / `env.ANTHROPIC_API_KEY` — API key forwarded to the SDK query
   *     (`cloud: default` only; no key falls back to OAuth credentials).
   *   - `cloud` / `cloudConfig` / `cloudSecrets` — non-default clouds (bedrock/vertex/azure/
   *     gateway) route through `buildClaudeCloudEnv` instead: exactly one `CLAUDE_CODE_USE_*`
   *     flag plus the resolved credential bundle, never `queryArgs.apiKey`.
   *   - `agentName` — name of a `.claude/agents/<name>.md` agent definition to use as identity.
   *   - `mcpServers` — in-process MCP server instances injected as native `tool_use` tools.
   *   - `systemPrompt` — appended to the `claude_code` preset system prompt.
   *   - `resumeSessionId` — SDK session UUID to resume from a prior driver lifetime.
   *   - `maxTurns` — maximum agentic turns per `prompt()` call (default: 15).
   */
  constructor(config: AgentConfig) {
    super();
    this.config = config;
  }

  override get runtimeSessionId(): string | undefined {
    return this.sessionId || undefined;
  }

  override reconfigure(patch: Partial<Pick<AgentConfig, "model" | "thinking" | "effort">>): void {
    if (patch.model !== undefined) this.config.model = patch.model;
    if (patch.thinking !== undefined) this.config.thinking = patch.thinking;
    if (patch.effort !== undefined) this.config.effort = patch.effort;
  }

  override getModel(): string | undefined {
    return this.config.model;
  }

  /**
   * Dynamically imports `@anthropic-ai/claude-agent-sdk`.
   *
   * @throws {Error} When the SDK package is not installed. Install it with
   *   `bun add @anthropic-ai/claude-agent-sdk`.
   */
  public async start(): Promise<void> {
    try {
      this.sdk = await import("@anthropic-ai/claude-agent-sdk");
      this.log.info("Claude Agent SDK loaded");
    } catch {
      throw new Error(
        "Claude Agent SDK not installed. Run: bun add @anthropic-ai/claude-agent-sdk",
      );
    }
  }

  /**
   * Sends a user message to the Claude Agent SDK and resolves when the turn completes.
   *
   * If a live SDK query already exists (from a prior turn in this session) the message
   * is pushed into its persistent prompt stream (streaming input mode). Otherwise a new
   * query is started applying the resume chain described in the class-level remarks.
   *
   * @param message - Plain-text user prompt.
   * @throws {Error} When the SDK reports a fatal error that cannot be auto-recovered.
   *
   * @remarks
   * A single automatic retry is performed when the SDK throws
   * "No conversation found with session ID …" — a recoverable stale-resume error that
   * occurs after a container crash that prevented the SDK from flushing its session file.
   */
  public prompt(message: string): Promise<void> {
    // Question denial must overtake the queue so the active turn can unwind.
    this.denyPendingQuestion(QUESTION_SUPERSEDED_BY_PROMPT);

    const queuedPrompt = this.promptTail.then(() => this.runPrompt(message, freshRetryBudget()));
    this.promptTail = queuedPrompt.then(
      () => undefined,
      () => undefined,
    );
    return queuedPrompt;
  }

  /**
   * Runs one complete logical prompt inside its acquired FIFO slot.
   *
   * Retries recurse here rather than through {@link prompt}, so they cannot
   * enqueue behind themselves and deadlock.
   */
  private async runPrompt(message: string, _budget: RetryBudget): Promise<void> {
    if (!this.sdk) await this.start();

    this.authSelfHealUsed = _budget.auth;
    this.limitFailoverUsed = _budget.limit;
    // Per-attempt, not per-turn: the replay after a failover must be judged on
    // whether the NEW seat rejected it, not on the old seat's rejection.
    this.limitRejectionSeen = false;
    this.limitTurnRejectedForFailover = false;
    this.providerResponseReported = false;
    this.lastLimitInfo = null;
    this.deferredTurnError = null;

    // When the catch block decides to recurse (stale-resume or auth-error
    // retry), the recursive runPrompt() sets fresh turnResolve / turnReject on
    // `this` synchronously before its first `await`. If the outer finally
    // ran unconditionally it would clobber those handlers, leaving the
    // recursive turn's promise unresolvable — completeTurn would see
    // `this.turnResolve === null` and the recursive prompt() would hang
    // forever. The flag tells the finally to skip state-reset on the
    // retry path.
    //
    // EVERY retry branch in the catch block MUST set `retrying = true`
    // before returning the recursive call. Adding another retry branch
    // without setting this flag will re-introduce a hard-to-diagnose
    // hang: the recursive runPrompt's turnPromise never resolves because
    // its resolver was nulled by this function's finally.
    let retrying = false;

    // Clear last-turn usage on entry, except when this is a retry recursion
    // (auth-error / stale-resume / poison) — the retry shares the same logical
    // turn so we keep prior usage available until the SDK reports a fresh value.
    // NOTE: this must list EVERY `RetryBudget` field. Adding a new recovery kind
    // without extending this conjunction would incorrectly treat its retry as a
    // first attempt and wipe `_lastTokens` mid-turn.
    const isFirstAttempt =
      !_budget.staleResume &&
      !_budget.poison &&
      !_budget.auth &&
      !_budget.queryEnded &&
      !_budget.limit;
    if (isFirstAttempt) {
      this._lastTokens = null;
      this._lastContextTokens = null;
      this._lastModelUsage = null;
    }

    this.running = true;
    this.prevText = "";
    this.emittedSanitizedLen = 0;
    this.suppressingAuthStream = false;
    this.suppressedAuthRenderText = null;
    this.turnCompleted = false;
    // A grace timer left armed by the previous turn would fail this one.
    this.disarmErrorResultGrace();

    // A resident query that cannot accept another turn must be dropped BEFORE
    // the per-turn resolver is installed: recycling ends the old consumer loop,
    // and its guarded finally must never race the new turn's resolver.
    if (
      this.query &&
      (this.inputStream == null || this.inputStream.ended || this.shouldRecycleQuery())
    ) {
      this.recycleQueryForNextTurn();
    }

    // Install the per-turn resolver before we send any input. Fast turns can
    // complete on the next microtask; if the result lands before turnResolve is
    // assigned, completeTurn() has nothing to resolve and prompt() hangs forever.
    const turnPromise = new Promise<void>((resolve, reject) => {
      this.turnResolve = resolve;
      this.turnReject = reject;
    });
    try {
      if (this.query && this.inputStream) {
        // Feed the live query through its persistent prompt stream — stdin
        // stays open across turns in streaming input mode.
        this.inputStream.push(this.buildUserMessage(message));
      } else {
        // No live query — start a new one (continue: true resumes session)
        await this.startQuery(message);
      }
    } catch (err) {
      this.disarmErrorResultGrace();
      this.turnResolve = null;
      this.turnReject = null;
      this.running = false;
      throw err;
    }

    // The SDK can stop emitting without ever yielding a `result` and without
    // throwing (e.g. an upstream 429 the CLI renders as plain assistant text).
    // Nothing else settles `turnPromise`, so guard it with an inactivity timer.
    this.armTurnStallWatchdog();

    // Wait for this turn to complete
    try {
      await turnPromise;
    } catch (err) {
      // ── Query-ended-before-turn retry ───────────────────────────────────
      // The turn was pushed into a resident query whose generator was already
      // ending, so no CLI ever received it (see `rejectTurnSilently`). Nothing
      // about the prompt failed — replay it. `this.query` was nulled before
      // the rejection, so the recursion spawns a fresh CLI with `--resume` and
      // the conversation continues unbroken.
      if (!_budget.queryEnded && err instanceof QueryEndedBeforeTurnError) {
        this.log.warn("turn was enqueued into an ending query; replaying on a fresh one");
        retrying = true;
        return this.runPrompt(message, { ..._budget, queryEnded: true });
      }
      // The Claude Agent SDK throws "No conversation found with session ID
      // <uuid>" when we try to resume a session whose JSONL persistence was
      // lost — most commonly after a container crash that killed the query
      // mid-stream, so the driver captured the session id but the SDK never
      // flushed the session file to disk. The resume is permanently broken;
      // retry the prompt once with a fresh session so the user can continue
      // (and emit a non-fatal warning so the UI can surface that Claude's
      // short-term memory was dropped).
      const errMsg = err instanceof Error ? err.message : String(err);
      const isStaleResume =
        !_budget.staleResume && /No conversation found with session ID/i.test(errMsg);
      if (isStaleResume) {
        this.prepareStaleResumeRetry();
        retrying = true;
        return this.runPrompt(message, { ..._budget, staleResume: true });
      }
      // ── Poisoned-transcript self-heal ──────────────────────────────────
      // The Anthropic API permanently rejects a conversation once its history
      // contains a malformed content block. Every replayed turn 400s with
      // `invalid_request_error`, bricking the session with no in-band recovery.
      // `scrubPoisonedTranscript` repairs three known poison classes on the
      // on-disk SDK transcript: image `media_type` mismatch/omission, oversized
      // images, and `cache_control` on empty text blocks (Claude Code bugs
      // #55338 / #30124 / #33179 / #34566 / #59626). The resume is retried once.
      // Unlike the stale-resume path above, this preserves the conversation —
      // the same session id is resumed against the now-clean transcript.
      //
      // The detection regex is intentionally broad: if it matches a 400 the
      // scrubber cannot actually repair, `scrub.changed` stays `false`, this
      // branch falls through, and the original error is rethrown. The
      // `!_budget.poison` guard caps recovery at a single scrub retry regardless.
      const isPoisonedHistory =
        !_budget.poison &&
        /invalid_request_error/i.test(errMsg) &&
        /media[_ ]?type|could not process image|image exceeds|cache_control/i.test(errMsg);
      const poisonSessionId = this.config.resumeSessionId || this.sessionId;
      if (isPoisonedHistory && poisonSessionId) {
        if (await this.attemptPoisonScrubRetry(poisonSessionId)) {
          retrying = true;
          return this.runPrompt(message, { ..._budget, poison: true });
        }
      }
      // ── Auth-error retry: ask the runner to refresh the credential, then
      // replay the in-flight prompt once. Centralising this inside the driver
      // means every `driver.prompt(...)` call site (serve handler, compaction
      // orchestrator, flow orchestrator, future call sites) gets self-healing
      // for free — no per-call-site wrapping. The runner provides the
      // `onAuthError` callback in `AgentConfig`; standalone CLI / forge
      // sessions leave it unset and surface the original `AuthError`.
      //
      // The prompt FIFO gives one logical turn exclusive ownership of the
      // consumer loop, so only one auth failure can reach this path at a time.
      // The runner's previous in-flight Map (Step 9b of the original spec)
      // existed to coalesce the per-call-site wrappers and is unnecessary
      // here.
      //
      // Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
      // § "Runner-side handling on 401" (moved bridge-side).
      //
      // ── Seat failover: the turn was blocked by an upstream USAGE limit ──
      //
      // Auth and limit recovery are mutually exclusive so exactly one owns the
      // failure — and therefore exactly one emits the error `failTurn`
      // deferred. `authOwns` is deliberately the same predicate `failTurn`
      // evaluates, so the branch that deferred is the branch that resolves.
      // Note `authOwns` folds in "an auth mediator exists": an `AuthError` on a
      // driver with no `onAuthError` falls through to the seat failover, which
      // is wanted — a parked OAuth seat routinely surfaces as
      // `authentication_error`, so the seat switch is the real recovery there.
      //
      // The limit path exists because a parked seat was previously reachable
      // only through a token refresh: `handleRateLimitEvent` logged the
      // `rejected` telemetry and returned, so a session sat on a dead seat
      // until the 30-minute stall watchdog fired (skaile-ai/platform#2976).
      const authOwns = err instanceof AuthError && !_budget.auth && this.config.onAuthError != null;
      if (authOwns) {
        if (await this.attemptAuthSelfHeal(err)) {
          retrying = true;
          return this.runPrompt(message, { ..._budget, auth: true });
        }
      } else if (!_budget.limit && this.limitRejectionSeen && this.config.onLimitBlocked) {
        if (await this.attemptLimitFailover(err)) {
          // The failover round trip can sit for tens of seconds. A cancel that
          // landed inside it means the user no longer wants this prompt —
          // replaying it would re-send exactly what they just cancelled.
          if (this.abortController?.signal.aborted) {
            this.log.info("aborted during the seat failover; not replaying the prompt");
            // `attemptLimitFailover` already nulled `query` and cleared
            // `running`, so `abort()` takes its no-query path and never reaches
            // `completeTurn()`. Without this the turn produces NO terminal
            // event at all — the deferred error was dropped by the switch and
            // the `finally` clears it — and every awaiter of the turn tail
            // hangs to its timeout.
            this.emitAgentEnd();
            return;
          }
          retrying = true;
          return this.runPrompt(message, { ..._budget, limit: true });
        }
        // Failover declined. Undo a rejection we manufactured purely to reach
        // it (see `limitTurnRejectedForFailover`); a turn that rejected on its
        // own — consumer throw, watchdog — still rejects.
        if (this.limitTurnRejectedForFailover) {
          this.emitAgentEnd();
          return;
        }
      }
      throw err;
    } finally {
      // Skip state reset when we recursed — the inner runPrompt() owns the
      // turnResolve / turnReject handlers and we must not clobber them.
      // See the `retrying` flag declared at the top of this function.
      if (!retrying) {
        this.disarmTurnStallWatchdog();
        this.disarmErrorResultGrace();
        this.turnResolve = null;
        this.turnReject = null;
        this.running = false;
        // Scope the limit state to the turn that observed it. The streaming
        // query outlives the turn, so a flag left armed here would still be
        // armed when a between-turns consumer error arrives.
        this.limitRejectionSeen = false;
        this.limitTurnRejectedForFailover = false;
        this.lastLimitInfo = null;
        this.deferredTurnError = null;
        // Same scoping reason: a stash left set here would let a between-turns
        // result classify as auth on the previous turn's evidence.
        this.suppressedAuthRenderText = null;
      }
    }
  }

  /**
   * Arm the per-turn inactivity watchdog. Self-rescheduling rather than a single
   * absolute timer so any mid-turn SDK message pushes the deadline out; the
   * timer is `unref`'d so an armed watchdog never holds the process open.
   *
   * Two budget tiers: until the first SDK message of the turn arrives the
   * (small) {@link firstMessageStallMs} budget applies — silence there means
   * the query is deaf, not busy. Once anything arrives, the (large)
   * {@link turnStallMs} ceiling takes over, because a long-running tool call
   * legitimately produces no SDK messages. The budget is re-read every tick, so
   * the first message flips tiers without re-arming.
   */
  private armTurnStallWatchdog(): void {
    this.disarmTurnStallWatchdog();
    // A fast turn can already have settled inside startQuery()'s awaits.
    if (this.turnCompleted || this.turnReject == null) return;
    this.firstSdkMessageSeen = false;
    const currentBudget = () => (this.firstSdkMessageSeen ? turnStallMs() : firstMessageStallMs());
    const tick = () => {
      if (this.turnCompleted || this.turnReject == null) return;
      const budget = currentBudget();
      // A turn parked on an unanswered AskUserQuestion emits no SDK messages by
      // design, and approvals routinely sit for hours — that is a live turn
      // waiting on a human, not a wedged query.
      if (this.hasPendingQuestion()) {
        this.turnStallTimer = setTimeout(tick, budget);
        this.turnStallTimer.unref?.();
        return;
      }
      const idleMs = Date.now() - this.lastSdkMessageAt;
      if (idleMs < budget) {
        this.turnStallTimer = setTimeout(tick, budget - idleMs);
        this.turnStallTimer.unref?.();
        return;
      }
      this.onTurnStalled(idleMs);
    };
    this.lastSdkMessageAt = Date.now();
    this.turnStallTimer = setTimeout(tick, currentBudget());
    this.turnStallTimer.unref?.();
  }

  private disarmTurnStallWatchdog(): void {
    if (this.turnStallTimer) {
      clearTimeout(this.turnStallTimer);
      this.turnStallTimer = null;
    }
  }

  /**
   * Bound the wait for the follow-up throw an `is_error` result implies. The
   * throw carries the real, richly-classified error, so the deferral stays —
   * this only caps it, turning a permanently pending turn into a bounded
   * failure. Never arms onto an already-settled turn: an auth / account / limit
   * payload has run `failTurn` by the time this is reached, and the same guard
   * is re-checked inside the callback because the throw normally lands in
   * between. Note 2026-06-08-claude-sdk-error-result-handling.
   */
  private armErrorResultGrace(
    detail: AgentError,
    context: { subtype: string; terminalReason?: string },
  ): void {
    this.disarmErrorResultGrace();
    if (this.turnCompleted || this.turnReject == null) return;
    this.errorResultGraceTimer = setTimeout(() => {
      this.errorResultGraceTimer = null;
      if (this.turnCompleted || this.turnReject == null) return;
      this.log.error("is_error result never produced its follow-up throw; failing the turn", {
        subtype: context.subtype,
        terminalReason: context.terminalReason,
        category: detail.category,
      });
      // A plain Error, never an AuthError: classification returned `unknown`,
      // and an AuthError here would fire the credential self-heal on a guess.
      // `detail.message` is carried in step with the surfaced text, as every
      // other failTurn site does — a consumer rendering either sees one story.
      const message =
        "The model reported an error but sent no details. " +
        "The conversation is intact — send your message again.";
      // The query is left registered on purpose (this driver settles and reuses
      // it for every other error result), so a throw arriving after expiry
      // lands on whichever turn is then in flight.
      this.failTurn(new Error(message), { ...detail, message });
    }, errorResultGraceMs());
    this.errorResultGraceTimer.unref?.();
  }

  /**
   * The retry helpers that null `turnResolve`/`turnReject` before recursing
   * need no call: they run only from `runPrompt`'s catch, which is reached only
   * because `failTurn` or `rejectTurnSilently` rejected — and both disarm first.
   */
  private disarmErrorResultGrace(): void {
    if (this.errorResultGraceTimer) {
      clearTimeout(this.errorResultGraceTimer);
      this.errorResultGraceTimer = null;
    }
  }

  /**
   * The SDK went silent mid-turn. Tear the query down so the *next* prompt
   * starts a fresh one (a live-but-deaf query would otherwise swallow every
   * subsequent `streamInput`), then reject the turn with an actionable message.
   */
  private onTurnStalled(idleMs: number): void {
    const idle =
      idleMs < 60_000 ? `${Math.round(idleMs / 1000)}s` : `${Math.round(idleMs / 60_000)}m`;
    this.log.error("turn stalled — no SDK message received; resetting the query", {
      idleMs,
      firstMessageOfTurnSeen: this.firstSdkMessageSeen,
    });
    // Snapshot BEFORE hardResetQuery() touches anything — see
    // snapshotDescendantPids's doc for why the order matters.
    const descendants = this.snapshotDescendantPids();
    this.hardResetQuery();
    this.killPids(descendants);
    this.failTurn(
      new Error(
        `Agent stalled: no response from the model process for ${idle}. ` +
          "The conversation is intact — send your message again.",
      ),
    );
  }

  /**
   * Drop a query that will not unwind on its own, keeping the session intact.
   *
   * Deliberately does NOT clear `sessionId` / `hasSession` — the next
   * `startQuery()` must resume the same SDK session so the conversation
   * survives. That is what separates this from {@link kill}.
   *
   * Does NOT sweep the CLI's descendant processes — callers needing that must
   * snapshot via {@link snapshotDescendantPids} *before* this (and any other
   * abort signal) runs, then {@link killPids} after.
   */
  private hardResetQuery(): void {
    this.endInputStream();
    if (this.query) {
      try {
        this.query.close();
      } catch {
        /* best effort */
      }
      this.query = null;
    }
    // Signal but do NOT null the controller: `handleConsumerError` and the
    // consumer loop read `signal.aborted` to tell an intentional unwind from a
    // real failure, and `startQuery()` installs a fresh one on the next turn.
    this.abortController?.abort();
  }

  /**
   * End the persistent prompt stream (the SDK responds by closing the CLI's
   * stdin) and forget it. Called wherever the query is dropped so a stale
   * stream can never be pushed into. Safe to call with no stream.
   */
  private endInputStream(): void {
    if (this.inputStream) {
      this.inputStream.end();
      this.inputStream = null;
    }
  }

  /**
   * `maxTurns` counts agentic turns, but whether the CLI resets that count per
   * user turn or accumulates it across a resident streaming session is
   * undocumented. This guard is correct under either semantics: once the last
   * result's reported `num_turns` is within {@link RECYCLE_TURNS_FRACTION}
   * headroom of the ceiling, the resident query is dropped at the next turn
   * boundary and the prompt respawns with `--resume` (which restarts the
   * count). Under per-turn semantics this fires only when a single prompt
   * nearly exhausted the budget by itself — where a recycle is harmless.
   */
  private shouldRecycleQuery(): boolean {
    if (this.lastNumTurns == null) return false;
    const max = this.config.maxTurns ?? 15;
    // Two triggers: the fraction catches long-lived sessions with a large
    // budget; the absolute headroom floor keeps small budgets (default 15)
    // from entering a turn with only a handful of agentic turns left.
    const threshold = Math.min(
      Math.max(1, Math.floor(max * RECYCLE_TURNS_FRACTION)),
      Math.max(1, max - MIN_RECYCLE_HEADROOM),
    );
    return this.lastNumTurns >= threshold;
  }

  /**
   * Gracefully drop the resident query between turns so the next prompt starts
   * a fresh CLI resuming the same session. Deliberately does NOT sweep the
   * CLI's descendant processes: a between-turns recycle mirrors the legacy
   * spawn-per-turn behavior, where the CLI's background tasks (`run_in_background`
   * dev servers) outlive the turn boundary. Note they become orphans no later
   * sweep can reach — snapshotDescendantPids walks from `cliProcessPid`, which
   * the next spawn overwrites — exactly as in legacy spawn-per-turn.
   */
  private recycleQueryForNextTurn(): void {
    this.log.info("recycling resident query before the next turn", {
      reportedTurns: this.lastNumTurns,
      maxTurns: this.config.maxTurns ?? 15,
      inputStreamEnded: this.inputStream?.ended ?? null,
    });
    this.endInputStream();
    if (this.query) {
      try {
        this.query.close();
      } catch {
        /* best effort */
      }
      this.query = null;
    }
    this.abortController?.abort();
  }

  /**
   * Capture every current OS descendant of the CLI subprocess (SIGKILLed
   * later via {@link killPids}). **Call BEFORE `interrupt()` /
   * `abortController.abort()` / `query.close()` — never after**: once the CLI
   * exits, Linux reparents its children immediately, so a later walk finds
   * nothing. Linux-only; `[]` if there's no captured pid or no `/proc`. Full
   * rationale: `_devlog/notes/2026-08-12-abort-kills-tool-subprocess.md`.
   */
  private snapshotDescendantPids(): number[] {
    const rootPid = this.cliProcessPid;
    if (rootPid == null) return [];
    return this.collectDescendantPids(rootPid);
  }

  /** SIGKILL every pid in a snapshot from {@link snapshotDescendantPids}. */
  private killPids(pids: number[]): void {
    if (pids.length === 0) return;
    let killed = 0;
    for (const pid of pids) {
      try {
        process.kill(pid, "SIGKILL");
        killed++;
      } catch {
        /* already exited — fine */
      }
    }
    this.log.info("swept CLI descendant processes on abort", {
      attempted: pids.length,
      killed,
    });
  }

  /** Direct children of `pid` via Linux procfs — no external binary required. */
  private childPids(pid: number): number[] {
    try {
      const raw = readFileSync(`/proc/${pid}/task/${pid}/children`, "utf8");
      return raw.trim().split(/\s+/).filter(Boolean).map(Number);
    } catch {
      return [];
    }
  }

  /** Every descendant of `rootPid` (excluding `rootPid` itself), breadth-first. */
  private collectDescendantPids(rootPid: number): number[] {
    const descendants: number[] = [];
    let frontier = [rootPid];
    while (frontier.length > 0) {
      const next: number[] = [];
      for (const pid of frontier) {
        for (const child of this.childPids(pid)) {
          descendants.push(child);
          next.push(child);
        }
      }
      frontier = next;
    }
    return descendants;
  }

  /**
   * Side-effects of the stale-resume recovery path: emit the resume-outcome and
   * non-fatal error events, then reset session state so the recursive prompt()
   * starts a fresh session. The caller owns the decision to recurse — this only
   * performs the state mutation, never control flow.
   */
  private prepareStaleResumeRetry(): void {
    const staleId = this.config.resumeSessionId || this.sessionId || "unknown";
    this.log.warn("stale Claude Code session, retrying with a fresh session", { staleId });
    // Structured event for the platform's resume outcome listener.
    // Emitted before the non-fatal error so the gateway records the
    // outcome regardless of what surfaces the error string.
    this.emit("agent-event", {
      type: "resume_failed",
      resumeSessionId: staleId,
      reason: "jsonl_lost",
    } satisfies AgentEvent);
    this.emit("agent-event", {
      type: "error",
      error:
        "Claude Code session state was lost (likely a prior crash). Restarting with a fresh session — previous conversation context is not available.",
      fatal: false,
    } satisfies AgentEvent);
    this.sessionId = "";
    this.hasSession = false;
    this.query = null;
    this.endInputStream();
    // Clear the config override too — leaving it in place would make the
    // retry re-trigger the same failure via startQuery's resume chain.
    this.config.resumeSessionId = undefined;
    this.turnResolve = null;
    this.turnReject = null;
    this.running = false;
  }

  /**
   * Side-effects of the poisoned-transcript recovery path: scrub the on-disk
   * transcript and, when something was repaired, emit recovery events and reset
   * query state. Returns `true` when the scrub changed the transcript (the
   * caller should recurse) and `false` otherwise. The caller owns the decision
   * to recurse — this only performs the scrub + state mutation, never control
   * flow.
   */
  private async attemptPoisonScrubRetry(poisonSessionId: string): Promise<boolean> {
    const scrub = await scrubPoisonedTranscript({
      configDir: this.resolveClaudeConfigDir(),
      sessionId: poisonSessionId,
      log: this.log,
    });
    if (scrub.changed) {
      this.log.warn("scrubbed poisoned Claude Code transcript, retrying resume", {
        sessionId: poisonSessionId,
        corrected: scrub.corrected,
        stubbed: scrub.stubbed,
        cacheStripped: scrub.cacheStripped,
      });
      // Structured event for the platform's resume outcome listener.
      this.emit("agent-event", {
        type: "resume_failed",
        resumeSessionId: poisonSessionId,
        reason: "jsonl_poisoned",
      } satisfies AgentEvent);
      const plural = (n: number) => (n === 1 ? "" : "s");
      const repairs: string[] = [];
      if (scrub.corrected > 0)
        repairs.push(`${scrub.corrected} media type${plural(scrub.corrected)} corrected`);
      if (scrub.stubbed > 0) repairs.push(`${scrub.stubbed} image${plural(scrub.stubbed)} removed`);
      if (scrub.cacheStripped > 0)
        repairs.push(
          `${scrub.cacheStripped} malformed block${plural(scrub.cacheStripped)} cleaned`,
        );
      this.emit("agent-event", {
        type: "error",
        error: `Recovered corrupt data in the conversation history (${repairs.join(", ")}). Retrying — earlier context is preserved.`,
        fatal: false,
      } satisfies AgentEvent);
      // Drop the dead query; keep sessionId / resumeSessionId so startQuery
      // resumes the now-clean transcript.
      this.query = null;
      this.endInputStream();
      this.turnResolve = null;
      this.turnReject = null;
      this.running = false;
      return true;
    }
    this.log.warn("poisoned-transcript error but scrub found nothing to repair", {
      sessionId: poisonSessionId,
      filePath: scrub.filePath,
    });
    return false;
  }

  /**
   * Side-effects of the auth-error recovery path: invoke the `onAuthError`
   * refresh callback. On success, reset query state and return `true` (the
   * caller should recurse). On failure, emit the deferred `agent-event: error`
   * (paired with the deferral in {@link failTurn}) and return `false`. The
   * caller owns the decision to recurse and the subsequent rethrow — this only
   * performs the refresh + state mutation, never control flow.
   */
  private async attemptAuthSelfHeal(err: AuthError): Promise<boolean> {
    this.log.info("auth error caught; invoking onAuthError refresh callback");
    // Protocol v3: the callback returns a typed `CredentialMint`. A
    // success branch (`ok: true`) means the runner has provisioned a
    // fresh credential and the driver should retry. A failure branch
    // (`ok: false`) carries a stable `code` the runner already
    // surfaced upstream; the driver surfaces the original `AuthError`
    // to its caller without retrying.
    let refreshed = false;
    try {
      const mint = await this.config.onAuthError!({
        configId: this.config.aiProviderConfigId ?? "",
      });
      refreshed = mint.ok;
      if (!mint.ok) {
        this.log.info("onAuthError reported refresh failure; surfacing AuthError", {
          code: mint.code,
        });
      }
    } catch (refreshErr) {
      this.log.warn("onAuthError callback threw; surfacing original AuthError", {
        error: refreshErr instanceof Error ? refreshErr.message : String(refreshErr),
      });
    }
    if (refreshed) {
      this.log.info("credentials refreshed; retrying prompt");
      // Drop the dead SDK query so the next prompt() invocation creates a
      // fresh one (which reads the rewritten credentials file). Keep
      // sessionId / hasSession so the new query resumes the same Claude
      // SDK session.
      this.query = null;
      this.endInputStream();
      this.turnResolve = null;
      this.turnReject = null;
      this.running = false;
      return true;
    }
    // Self-heal was attempted and failed (callback declined or threw).
    // `failTurn` suppressed the `agent-event: error` emission so the
    // refresh attempt could play out silently; emit it now before the
    // AuthError surfaces to the caller. Pairs with the deferral in
    // `failTurn` — every auth failure produces exactly one downstream
    // error event, either on the retry's `failTurn` (when self-heal
    // succeeded but the retry itself failed) or here.
    this.emitDeferredTurnError({ type: "error", error: err.message, detail: err.detail });
    return false;
  }

  /**
   * Side-effects of the seat-failover path: ask the host to re-resolve the AI
   * provider config behind this session. On `switched: true` drop the query and
   * return `true` (the caller recurses onto a fresh CLI); otherwise emit the
   * deferred error and return `false`. Mirrors {@link attemptAuthSelfHeal} —
   * this performs the state mutation, the caller owns the control flow.
   *
   * Dropping `this.query` is the whole point of the retry, not bookkeeping: a
   * resident CLI caches "blocked until <reset>" and short-circuits the next
   * prompt locally with no API attempt at all (skaile-ai/workspaces#566), so a
   * rewritten credentials file alone would change nothing. Only a fresh spawn
   * clears it. `sessionId` / `hasSession` are preserved so the new query
   * resumes the same conversation.
   */
  private async attemptLimitFailover(err: unknown): Promise<boolean> {
    this.log.warn("turn blocked by an upstream usage limit; invoking onLimitBlocked", {
      configId: this.config.aiProviderConfigId,
      limitType: this.lastLimitInfo?.limitType,
      resetsAt: this.lastLimitInfo?.resetsAt,
    });
    let outcome: LimitFailoverResult | undefined;
    try {
      outcome = await this.config.onLimitBlocked!({
        configId: this.config.aiProviderConfigId ?? "",
        ...(this.lastLimitInfo ?? {}),
      });
    } catch (failoverErr) {
      this.log.warn("onLimitBlocked callback threw; surfacing the original limit error", {
        error: failoverErr instanceof Error ? failoverErr.message : String(failoverErr),
      });
    }
    if (outcome?.switched === true) {
      this.log.info("switched AI provider config; respawning the CLI and replaying the prompt", {
        configId: outcome.configId,
      });
      this.query = null;
      this.endInputStream();
      this.turnResolve = null;
      this.turnReject = null;
      this.running = false;
      return true;
    }
    this.log.info("no alternative AI provider config available; surfacing the limit error", {
      reason: outcome?.reason,
    });
    const detail = (err as { detail?: AgentError } | null)?.detail;
    this.emitDeferredTurnError({
      type: "error",
      error: err instanceof Error ? err.message : String(err),
      ...(detail ? { detail } : {}),
    });
    return false;
  }

  /**
   * Resolve the Claude Code config directory — the parent of `projects/` — from
   * the driver config, the process environment, or the `~/.claude` default.
   */
  private resolveClaudeConfigDir(): string {
    return resolveClaudeConfigDir(this.config.env);
  }

  /**
   * Preventively repair the on-disk SDK transcript before a resume.
   *
   * The reactive {@link scrubPoisonedTranscript} pass in `prompt()` only fires
   * *after* a turn has already failed with a `400 invalid_request_error`,
   * costing a wasted round-trip and surfacing a scary (if non-fatal) error to
   * the user. A transcript poisoned in a prior driver lifetime — most commonly
   * an image block whose `media_type` does not match its bytes, produced by the
   * Claude Code `Read` tool on a PDF with embedded JPEGs (anthropics/claude-code
   * #55338) — would otherwise 400 on the very first resumed turn.
   *
   * Running the same magic-byte scrub *before* handing the transcript to the
   * SDK means a known poison class never reaches the API, so recovery is
   * invisible. This is regex-free (unlike the reactive gate) and idempotent: a
   * clean transcript is left byte-for-byte untouched. The reactive path remains
   * the safety net for poison introduced mid-turn within the current lifetime.
   */
  private async preventivelyScrubTranscript(): Promise<void> {
    const scrubStartedAt = Date.now();
    const sessionId = this.config.resumeSessionId || this.sessionId;
    if (!sessionId) {
      // Fresh session — no transcript to repair yet. Said out loud, because an
      // absent phase line would otherwise mean any of "nothing to do", "threw"
      // or "never reached" — useless for a number meant to settle an argument.
      this.log.info("transcript scrub skipped", {
        phase: "transcript_scrub",
        elapsedMs: 0,
        outcome: "no_session",
      });
      return;
    }
    try {
      const scrub = await scrubPoisonedTranscript({
        configDir: this.resolveClaudeConfigDir(),
        sessionId,
        log: this.log,
      });
      // A full parse+rewrite of the transcript on every first turn, so it scales
      // with conversation size — a suspected share of the resume wall-clock
      // (skaile-ai/workspaces#421).
      this.log.info("transcript scrub complete", {
        phase: "transcript_scrub",
        elapsedMs: Date.now() - scrubStartedAt,
        outcome: "ok",
        changed: scrub.changed,
      });
      if (scrub.changed) {
        this.log.warn("preventively scrubbed poisoned Claude Code transcript before resume", {
          sessionId,
          corrected: scrub.corrected,
          stubbed: scrub.stubbed,
          cacheStripped: scrub.cacheStripped,
        });
      }
    } catch (err) {
      // A scrub failure must never block the turn — the reactive path still
      // covers a poisoned block if one survives to the API.
      this.log.warn("preventive transcript scrub failed; continuing", {
        phase: "transcript_scrub",
        elapsedMs: Date.now() - scrubStartedAt,
        outcome: "failed",
        sessionId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Resolve the effective cloud transport for this query. Unrecognized
   * non-default values fall back to `default` (today's behavior) with a
   * warning — old-runner tolerance per the cloud-provider contract; the
   * platform gates runner versions for real non-default sessions.
   */
  private resolveCloud(): CloudId {
    const raw = this.config.cloud;
    if (!raw || raw === "default") return "default";
    if ((SUPPORTED_CLOUDS as readonly string[]).includes(raw)) return raw as CloudId;
    this.log.warn("unknown cloud value — falling back to the default transport", { cloud: raw });
    return "default";
  }

  private async startQuery(message: string): Promise<void> {
    this._querySpawnGeneration++;
    this.queryStartedAt = Date.now();
    this.awaitingFirstMessageOfSpawn = true;
    // Mirrors the resume chain in the options below. Read here because the
    // `jsonl_lost` fallback clears `config.resumeSessionId` before a re-spawn.
    this.spawnResumed = !!(this.config.resumeSessionId || this.sessionId || this.hasSession);
    this.abortController = new AbortController();

    // Repair any poisoned content block left in the on-disk transcript before
    // the SDK resumes it — converts the reactive post-400 self-heal into a
    // deterministic, user-invisible recovery. See the method doc for rationale.
    await this.preventivelyScrubTranscript();

    const cloud = this.resolveCloud();

    // API-key resolution applies to the default (native Anthropic API) cloud
    // only; non-default clouds carry their credentials in `cloudSecrets`.
    const apiKey =
      cloud === "default"
        ? this.config.apiKeys?.anthropic ||
          this.config.env?.ANTHROPIC_API_KEY ||
          process.env.ANTHROPIC_API_KEY
        : undefined;

    // When no apiKey is configured the SDK falls back to OAuth credentials
    // discovered via `settingSources: ["user"]` (i.e. `~/.claude/.credentials.json`).
    // The driver remembers this so the error classifier can branch on the
    // credential type — auth-shaped failures from OAuth subscriptions
    // overwhelmingly correlate with quota/rate-limit, not invalid tokens.
    // The OAuth fallback exists only on the default cloud.
    this.usingOauthCredential = cloud === "default" && !apiKey;

    const claudePath = this.findClaudeBinary();
    // Shared with the manual spawnClaudeCodeProcess below — replacing the
    // SDK's default spawn path means its own internal stderr → this callback
    // wiring no longer runs, so we have to do it ourselves with the exact
    // same handler (see that field's neighboring comment).
    const onStderr = (data: string) => this.log.warn("claude stderr", { line: data.trim() });

    const options: SdkOptions = {
      cwd: this.config.cwd,
      // Load project + user settings so .claude/skills/ are discovered
      settingSources: ["user", "project"],
      // Use full Claude Code tool preset so skills, agents, etc. work
      tools: { type: "preset", preset: "claude_code" },
      permissionMode: "bypassPermissions",
      allowDangerouslySkipPermissions: true,
      // AskUserQuestion routes through canUseTool even under bypassPermissions
      // (ask-rule evaluation precedes permission-mode evaluation in the SDK),
      // so this is the only place the turn can pause to collect a real answer.
      // Every other tool auto-approves and never reaches this callback.
      canUseTool: this.handleCanUseTool,
      abortController: this.abortController,
      model: this.config.model || "sonnet",
      maxTurns: this.config.maxTurns ?? 15,
      includePartialMessages: true,
      // Thinking mode — maps simple string config to SDK ThinkingConfig objects
      ...(this.config.thinking ? { thinking: this.mapThinkingConfig(this.config.thinking) } : {}),
      // Reasoning effort — passed directly to the SDK
      ...(this.config.effort ? { effort: this.config.effort } : {}),
      hooks: {},
      stderr: onStderr,
      // Spawn the CLI ourselves to capture its OS pid for snapshotDescendantPids()
      // (Query never exposes it otherwise). This replaces the SDK's own default
      // spawn, so we also have to re-wire child.stderr -> onStderr ourselves below.
      spawnClaudeCodeProcess: (spawnOpts) => {
        const child = spawn(spawnOpts.command, spawnOpts.args, {
          cwd: spawnOpts.cwd,
          env: spawnOpts.env,
          stdio: ["pipe", "pipe", "pipe"],
        });
        this.cliProcessPid = child.pid ?? null;
        child.stderr?.on("data", (chunk: Buffer) => onStderr(chunk.toString()));
        // Guard: a newer startQuery() may already have overwritten
        // cliProcessPid by the time this exit fires (a queued prompt can
        // start before this query finishes aborting) — don't clobber it.
        child.once("exit", () => {
          if (this.cliProcessPid === child.pid) this.cliProcessPid = null;
        });
        // Documented contract: this signal fires only after the SDK's own
        // stdin-EOF + grace window, so a plain child.kill() here is safe and
        // matches the default spawn behavior we're replacing.
        spawnOpts.signal.addEventListener("abort", () => {
          try {
            child.kill();
          } catch {
            /* best effort */
          }
        });
        return child;
      },
      ...(claudePath ? { pathToClaudeCodeExecutable: claudePath } : {}),
      ...(this.config.systemPrompt
        ? {
            systemPrompt: {
              type: "preset",
              preset: "claude_code",
              append: this.config.systemPrompt,
            },
          }
        : {}),
      // ── Session continuity ────────────────────────────────────────────────
      // Three-level fallback ensures the right session is always resumed:
      //   resumeSessionId — caller explicitly wants this session (skaile resume)
      //   sessionId       — a session was already started this driver lifetime (multi-turn)
      //   continue: true  — resume the most recent session in cwd (between restarts)
      //   (none)          — start fresh
      ...(this.config.resumeSessionId
        ? { resume: this.config.resumeSessionId }
        : this.sessionId
          ? { resume: this.sessionId }
          : this.hasSession
            ? { continue: true }
            : {}),
      // ── Connector tool injection ──────────────────────────────────────────
      // In-process MCP servers from ConnectorManager (buildSdkConnectorTools in resources.ts).
      // These expose skaile.yaml connectors as native tool_use calls inside the session.
      // omp does not use this — it falls back to `skaile res <id> <op>` CLI commands
      // documented in the system prompt section (buildConnectorPromptSection).
      //
      // Protocol v2: when `config.capabilities` is provided, the runner's
      // CapabilityRegistry contributes an additional `skaile-capabilities`
      // synthetic MCP server alongside any pre-existing `mcpServers`. Tool
      // calls into that server route back through `hooks.invoke()` so the
      // registry's per-handler logger fires and fire-and-forget / render
      // semantics apply uniformly.
      ...(await this.buildEffectiveMcpServers()),
      // ── Main-agent handoff ────────────────────────────────────────────────
      // agentName (from AgentConfig) is the equivalent of `claude --agent <name>`.
      // The SDK reads .claude/agents/<agentName>.md from the cwd; that file was
      // rendered by asset-manager (claudeCodeRenderer) during `skaile install`.
      // Its YAML frontmatter sets model / tools / maxTurns; its markdown body is the
      // assembled system prompt (SOUL + fragments + RULES + abilities + contracts).
      // Omitting agentName uses Claude Code's default identity (no custom agent file).
      ...(this.config.agentName ? { agent: this.config.agentName } : {}),
      // Tool restrictions from agent.yaml — always include a wildcard allow-entry for each
      // injected MCP server so connector tools aren't silently blocked by an allowlist.
      ...this.buildToolRestrictions(),
    };

    this.log.debug("query start", {
      model: this.config.model || "default",
      session: this.config.resumeSessionId || this.sessionId || this.config.sessionId || "new",
    });

    // The Claude Agent SDK reads the API key from the ANTHROPIC_API_KEY env var
    // (or OAuth creds), NOT the query({apiKey}) arg — which it ignores. Inject
    // the key into the SDK child env so API-key provider configs authenticate.
    let childEnv: Record<string, string>;
    if (cloud !== "default") {
      // Non-default clouds ALWAYS set env: the transport flag + credential
      // bundle must reach the SDK child even when no api key is in play.
      childEnv = buildClaudeCloudEnv({
        cloud,
        cloudConfig: this.config.cloudConfig,
        cloudSecrets: this.config.cloudSecrets,
        baseEnv: snapshotProcessEnv(),
        configEnv: this.config.env,
        // Gateway-only auth fallback so standalone sessions (settings-resolved
        // apiKeys, no platform bundle) still reach the proxy authenticated.
        fallbackApiKey: this.config.apiKeys?.anthropic,
      });
    } else if (apiKey) {
      childEnv = { ...snapshotProcessEnv(), ...(this.config.env ?? {}), ANTHROPIC_API_KEY: apiKey };
    } else {
      // OAuth path: the SDK would inherit process.env implicitly; snapshot it
      // (no config.env here — that would be a behaviour change) so the flag
      // below has somewhere to live.
      childEnv = snapshotProcessEnv();
    }
    // Set last so neither config.env nor a cloud bundle can re-enable it: a
    // subscription seat otherwise advertises the seat owner's personal claude.ai
    // connectors to the session (see the settings guard in runner/serve-credentials.ts).
    childEnv.ENABLE_CLAUDEAI_MCP_SERVERS = "false";
    options.env = childEnv;

    const { query } = this.sdk!;
    // Streaming input mode: a persistent AsyncIterable prompt, never a plain
    // string. A string prompt sets `isSingleUserTurn` inside the SDK, which
    // closes the CLI's stdin at the first `result` — and a CLI kept alive past
    // that close (live background tasks) silently drops every later write
    // (skaile-ai/workspaces#566). The stream outlives this turn: later prompts
    // push into it, and it ends only at driver-controlled teardown.
    this.inputStream = new PushableInputStream();
    this.inputStream.push(this.buildUserMessage(message));
    // Fresh spawn restarts the CLI's agentic-turn accounting.
    this.lastNumTurns = null;
    const queryArgs: {
      prompt: AsyncIterable<SDKUserMessage>;
      options: SdkOptions;
      apiKey?: string;
    } = {
      prompt: this.inputStream,
      options,
    };
    if (apiKey) queryArgs.apiKey = apiKey;

    this.query = query(queryArgs);
    // Intentionally not awaited — this is the long-lived message loop and must
    // not block prompt() from returning. It wraps its own body in try/catch,
    // but that catch itself emits, which can throw; a terminal handler here
    // keeps a late failure from surfacing as an unhandled rejection and taking
    // the process down.
    this.consumeMessages().catch((err: unknown) => {
      // Pass `err` positionally so normalizeError() keeps {name, message, stack}
      // — a handler for an otherwise process-killing failure is the last place
      // to throw the stack away. See core/CLAUDE.md § Logging.
      this.log.error("message loop terminated unexpectedly", err);
    });
  }

  override getSlashCommands(): SlashCommandInfo[] {
    return this.cachedCommands;
  }

  // ── Interactive questions (AskUserQuestion) ──────────────────────────────

  /**
   * Permission callback wired into every query. Only `AskUserQuestion` is
   * intercepted: the turn pauses here until {@link answerQuestion} resolves it
   * (or {@link denyPendingQuestion} cancels it). Every other tool auto-approves
   * — under `bypassPermissions` the SDK never even invokes this callback for
   * them, but we approve verbatim as a defensive default.
   *
   * Arrow property so the bound `this` survives being passed as
   * `options.canUseTool` by value.
   */
  private handleCanUseTool: CanUseTool = (toolName, input, opts) => {
    if (toolName !== "AskUserQuestion") {
      return Promise.resolve({ behavior: "allow", updatedInput: input });
    }

    // Parse the SDK's question array. The `question` field of each entry is the
    // key the SDK expects back in `answers`. The matching `question` events the
    // normalizer emits to the UI carry the same text in the same order, so
    // in-order replies map back cleanly.
    const rawQuestions = Array.isArray(input.questions) ? input.questions : [];
    const questionTexts = rawQuestions.map((q) =>
      String((q as Record<string, unknown>)?.question ?? ""),
    );

    // Defensive: a malformed call with no questions can't be answered — let it
    // through unchanged rather than pausing the turn forever.
    if (questionTexts.length === 0) {
      return Promise.resolve({ behavior: "allow", updatedInput: input });
    }

    // A prior question should already have resolved before the SDK issues a new
    // one (the turn is paused while we wait), but guard against a leak.
    this.denyPendingQuestion("superseded by a new question");

    return new Promise<PermissionResult>((resolve) => {
      this.pendingQuestion = {
        resolve,
        originalInput: input,
        questionTexts,
        answers: {},
      };
      // Deny-on-suspend: if the session aborts/hibernates while waiting, close
      // the tool call cleanly so the resumed transcript has no dangling
      // tool_use. abort()/kill()/resetSession() all abort this controller.
      opts.signal.addEventListener(
        "abort",
        () => this.denyPendingQuestion("session aborted before the question was answered"),
        { once: true },
      );
      this.log.debug("paused on AskUserQuestion", { questions: questionTexts.length });
    });
  };

  override hasPendingQuestion(): boolean {
    return this.pendingQuestion !== null;
  }

  override answerQuestion(answer: string, question?: string): boolean {
    const pending = this.pendingQuestion;
    if (!pending) return false;

    // A named answer binds to its own sub-question; an unnamed one fills the
    // next unanswered slot, correct only while the host answers top-down.
    // `hasOwn`, not `in`: a question reading "constructor" must not match.
    const key =
      question !== undefined && pending.questionTexts.includes(question)
        ? question
        : pending.questionTexts.find((q) => !Object.hasOwn(pending.answers, q));
    if (key !== undefined) {
      pending.answers[key] = answer;
    }

    // Resolve only once every sub-question has an answer — resuming the same
    // paused turn with the structured answers bound to the original tool_use.
    if (pending.questionTexts.every((q) => Object.hasOwn(pending.answers, q))) {
      this.pendingQuestion = null;
      // The turn resumes here, so the stall clock does too: a question that sat
      // unanswered longer than the budget would otherwise trip on the very next
      // tick, before the SDK has had a chance to respond to the answer.
      this.lastSdkMessageAt = Date.now();
      pending.resolve({
        behavior: "allow",
        updatedInput: { ...pending.originalInput, answers: pending.answers },
      });
      this.log.debug("answered AskUserQuestion", { questions: pending.questionTexts.length });
    }
    return true;
  }

  /**
   * Resolve any pending {@link handleCanUseTool} promise with a `deny` so the
   * SDK records a clean tool_result and the turn unwinds. No-op when nothing is
   * pending. Used on abort/kill/reset and when a question is superseded.
   */
  private denyPendingQuestion(reason: string): void {
    const pending = this.pendingQuestion;
    if (!pending) return;
    this.pendingQuestion = null;
    // Same restart as the answered path — the SDK unwinds the tool call from here.
    this.lastSdkMessageAt = Date.now();
    pending.resolve({ behavior: "deny", message: reason });
  }

  /**
   * Compose the MCP servers passed to the SDK query. Merges the legacy
   * `config.mcpServers` (connectors / workspace plugin / declarative skaile.yaml
   * entries) with a synthetic `skaile-capabilities` server built from the
   * runner-provided {@link BridgeCapabilityHooks} when present.
   *
   * Returns an empty options patch when neither source is configured, so legacy
   * v1 sessions remain unaffected.
   */
  private async buildEffectiveMcpServers(): Promise<Pick<SdkOptions, "mcpServers">> {
    const out: Record<string, unknown> = {};
    if (this.config.mcpServers) {
      Object.assign(out, this.config.mcpServers);
    }

    const hooks = this.config.capabilities;
    if (hooks) {
      const capServer = await this.buildCapabilityMcpServer();
      if (capServer) {
        out["skaile-capabilities"] = capServer;
      }
    }

    return Object.keys(out).length > 0 ? { mcpServers: out as SdkOptions["mcpServers"] } : {};
  }

  /**
   * Build the synthetic MCP server that exposes registered capabilities to the
   * Claude Agent SDK. Lazy-imports `createSdkMcpServer` from the SDK and `zod`
   * (an SDK peer) since both are optional peers of this bridge.
   *
   * Returns null when the SDK or zod is unavailable, or when the registry
   * exposes no tools — leaving the legacy `mcpServers` untouched.
   */
  private async buildCapabilityMcpServer(): Promise<unknown | null> {
    const hooks = this.config.capabilities;
    if (!hooks) return null;
    const tools = hooks.composeTools();
    if (tools.length === 0) return null;

    let sdk: any;
    try {
      sdk = await import("@anthropic-ai/claude-agent-sdk");
    } catch {
      this.log.warn("claude-agent-sdk unavailable; cannot build capability MCP server");
      return null;
    }
    // Static import below at module scope ensures bun --compile bundles zod
    // into the binary; dynamic import("zod") via a string variable evades
    // bun's static analysis and fails at runtime inside /$bunfs/.
    const z: any = zStatic;

    // Build per-tool entries. Schemas arrive from the registry as JSON Schema
    // (Zod -> z.toJSONSchema in `defineCapability`). The Claude SDK accepts
    // either a Zod object or a record of Zod fields; we mirror the existing
    // `buildSdkConnectorTools` path which uses an inputSchema record. The full
    // input contract is enforced server-side by `hooks.invoke()` (Zod parse).
    const sdkTools = tools.map((tool) => ({
      name: tool.name,
      description: tool.description,
      // Pass through the JSON Schema object directly. The SDK forwards it to
      // the LLM as the function parameter spec. Concrete validation runs in
      // `hooks.invoke()`, not the SDK.
      inputSchema: jsonSchemaToZodLoose(tool.parameters, z) ?? { args: z.unknown().optional() },
      handler: async (input: unknown) => {
        const callId = `cap-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        try {
          const dispatch = await dispatchCapability(hooks, callId, tool.name, input, (err) =>
            this.log.warn("background capability handler failed", {
              name: tool.name,
              error: String(err),
            }),
          );
          const text =
            typeof dispatch.result === "string"
              ? dispatch.result
              : JSON.stringify(dispatch.result ?? {});
          return { content: [{ type: "text" as const, text }] };
        } catch (err: any) {
          return {
            content: [{ type: "text" as const, text: `Error: ${err?.message ?? String(err)}` }],
            isError: true,
          };
        }
      },
    }));

    return sdk.createSdkMcpServer({ name: "skaile-capabilities", tools: sdkTools });
  }

  /** Fetch and cache slash commands from the SDK query. */
  private async fetchSlashCommands(): Promise<void> {
    if (!this.query) return;
    try {
      const commands = await this.query.supportedCommands();
      this.cachedCommands = commands.map((c) => ({
        name: c.name,
        description: c.description,
        argumentHint: c.argumentHint || undefined,
      }));
      this.emit("agent-event", {
        type: "commands_available",
        commands: this.cachedCommands,
      } satisfies AgentEvent);
      this.log.debug("discovered slash commands", { count: this.cachedCommands.length });
    } catch (err) {
      this.log.warn("failed to fetch slash commands", { error: String(err) });
    }
  }

  /**
   * Background loop — consumes the query's async generator continuously.
   * The generator stays alive between turns; streamInput() feeds new messages.
   * If it ends unexpectedly, query is nulled so the next prompt() restarts.
   */
  private async consumeMessages(): Promise<void> {
    // Capture the query this loop serves: retry/recycle paths install a NEW
    // query while an old consumer may still be unwinding, and a stale
    // consumer's catch/finally must never settle the new query's turn or null
    // the new query out from under it.
    const servedQuery = this.query;

    // Fetch slash commands in the background once the query is live.
    // `void` rather than a handler: the method already wraps its whole body in
    // a try/catch that only logs, so it cannot reject.
    void this.fetchSlashCommands();

    try {
      for await (const msg of servedQuery!) {
        // A retry/recycle can replace the query while this consumer is parked
        // on the iterator; a straggler from the replaced query (background-task
        // notifications, a late result) must not inject liveness, events, or a
        // turn-settle into the new query's turn.
        if (this.query !== servedQuery) break;
        if (this.abortController?.signal.aborted) break;

        // Any message counts as liveness for the stall watchdog — including
        // stream_event deltas and rate_limit_event telemetry. The first one of
        // a turn also flips the watchdog to the (large) mid-turn budget.
        this.lastSdkMessageAt = Date.now();
        if (this.awaitingFirstMessageOfSpawn) {
          this.awaitingFirstMessageOfSpawn = false;
          // Spawn-to-first-token, once per spawn. On a resumed spawn this covers
          // the SDK's own replay of the conversation, which no other log accounts
          // for — the suspected bulk of a slow wake.
          this.log.info("first sdk message", {
            phase: "query_first_message",
            elapsedMs: Date.now() - this.queryStartedAt,
            resumed: this.spawnResumed,
          });
        }
        this.firstSdkMessageSeen = true;

        this.captureSessionId(msg);

        this.handleSdkMessage(msg);

        // result = turn boundary — but only complete the turn when the
        // result was NOT an error. The Claude Agent SDK occasionally emits
        // `{type: "result", subtype: "success", is_error: true, errors: []}`
        // when an upstream API call (e.g. a 401) made the run fail; the
        // actual error text arrives ~200 ms later as a thrown exception
        // from this iterator. If we resolve the turn here on the
        // is_error result, the consumer-error catch below fires with
        // `turnReject` already null, the bridge's auth-retry path in
        // `prompt()` never runs, and the user sees a fatal 401. By
        // skipping `completeTurn` for is_error results, the turn stays
        // pending until the thrown exception lands in the catch and
        // `failTurn(AuthError)` rejects the promise with the real error.
        //
        // Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`.
        if (msg.type === "result" && !(msg as { is_error?: boolean }).is_error) {
          this.completeTurn();
        }
      }
    } catch (err: unknown) {
      if (this.query === servedQuery) {
        this.handleConsumerError(err);
      } else {
        this.log.debug("stale consumer error after query replacement; ignoring", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      this.log.debug("query generator ended");
      if (this.query === servedQuery) {
        // Drop the query BEFORE settling: a turn rejected below is about to be
        // replayed, and its `runPrompt` recursion must find no live query.
        this.query = null;
        this.endInputStream();
        // A turn that is still pending here and has not seen a single SDK
        // message was enqueued into this generator as it was already ending —
        // the CLI never received it. Resolving it would hand the caller an
        // empty, instant success for a turn the model never ran (the
        // threshold-compaction empty summary). Reject it as retryable
        // instead; a turn that DID see messages keeps the historical
        // completeTurn() semantics.
        //
        // The abort check is what keeps "retryable" honest: `abort()` is the
        // one teardown that leaves `this.query` set and lets this finally
        // settle the turn, so without it a turn cancelled before its first SDK
        // message would be replayed on a fresh CLI — re-sending the very
        // prompt the user just cancelled.
        const neverDispatched =
          !this.turnCompleted &&
          this.turnReject != null &&
          !this.firstSdkMessageSeen &&
          !this.abortController?.signal.aborted;
        if (neverDispatched) {
          this.rejectTurnSilently(
            new QueryEndedBeforeTurnError(
              "The model process ended before this turn was dispatched.",
            ),
          );
        } else {
          this.completeTurn();
        }
      }
    }
  }

  /** Capture the SDK session id from a message (first one assigns hasSession). */
  private captureSessionId(msg: SDKMessage): void {
    const sessionId = "session_id" in msg ? msg.session_id : undefined;
    if (sessionId) {
      if (!this.hasSession) {
        this.hasSession = true;
        this.log.debug("session id assigned", { sessionId });
      }
      this.sessionId = sessionId;
    }
  }

  /**
   * Handle an error thrown by the query's async generator. Aborts are logged
   * silently; everything else is classified and routed through {@link failTurn}
   * (auth-shaped failures promoted to {@link AuthError}).
   */
  private handleConsumerError(err: unknown): void {
    const error = err instanceof Error ? err : new Error(String(err));
    if (error.name === "AbortError" || this.abortController?.signal.aborted) {
      this.log.info("aborted");
      return;
    }
    const rawDetail = classifyClaudeSdkError({
      errors: [error.message],
      oauthCredential: this.usingOauthCredential,
    });
    // A 401 recurring after the self-heal refresh ran is an account failure,
    // not a credential one — downgrade so the message is actionable and the
    // self-heal stops looping. See _devlog/notes/2026-06-10-post-refresh-401-account.md.
    const detail =
      rawDetail.category === "auth" && this.authSelfHealUsed
        ? reclassifyAuthAsAccountUnavailable(rawDetail)
        : rawDetail;
    this.log.error("consumer error", {
      message: error.message,
      category: detail.category,
      retryable: detail.retryable,
    });
    // The dominant 401 shape reaches the driver HERE, not as an error result:
    // the SDK tags the run a success and the real `authentication_error`
    // arrives ~200 ms later as an iterator throw. Reporting only from the
    // result path would therefore miss most 401s.
    if (detail.category === "auth") {
      this.reportProviderResponse({ status: 401 });
    } else if (isSeatCapacityFailure(detail)) {
      this.limitRejectionSeen = true;
      this.reportProviderResponse({ status: 429, ...(this.lastLimitInfo ?? {}) });
    }
    // auth → AuthError (runner discriminates by instance); account →
    // AccountUnavailableError (NOT an AuthError, so the self-heal does not
    // re-fire); other categories → plain Error.
    const surfaced =
      detail.category === "auth"
        ? new AuthError(detail)
        : detail.category === "account"
          ? new AccountUnavailableError(detail)
          : error;
    this.failTurn(surfaced, detail);
  }

  /**
   * Signal the current turn as done — idempotent per turn.
   *
   * Same defensive ordering as {@link failTurn}: settle the turn promise
   * before emitting `agent_end` so a misbehaving listener cannot trap the
   * promise in a pending state. See failTurn's docblock for the production
   * incident that motivated this pattern.
   */
  private completeTurn(): void {
    if (this.turnCompleted) return;
    this.turnCompleted = true;
    this.disarmTurnStallWatchdog();
    this.disarmErrorResultGrace();
    if (this.turnResolve) {
      this.turnResolve();
      this.turnResolve = null;
      this.turnReject = null;
    }
    this.emitAgentEnd();
  }

  /** Emit the turn-end signal, swallowing a throwing listener. */
  private emitAgentEnd(): void {
    try {
      this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
    } catch (emitErr) {
      this.log.warn("agent-event listener threw while ending the turn; suppressing", {
        error: emitErr instanceof Error ? emitErr.message : String(emitErr),
      });
    }
  }

  /**
   * Reject the current turn WITHOUT emitting an `agent-event: error`.
   *
   * The one legitimate use is a turn that is about to be replayed: the host
   * must not see an error for a failure the driver is itself recovering from
   * (the same reasoning as `failTurn`'s auth self-heal deferral). Ordering
   * mirrors {@link failTurn} — settle the promise before touching anything
   * else — so a turn can never be left pending.
   *
   * Anything a caller should actually learn about goes through `failTurn`.
   */
  private rejectTurnSilently(err: Error): void {
    this.disarmTurnStallWatchdog();
    this.disarmErrorResultGrace();
    this.turnCompleted = true;
    if (this.turnReject) {
      const reject = this.turnReject;
      this.turnResolve = null;
      this.turnReject = null;
      reject(err);
    }
  }

  /**
   * Signal the current turn as failed. When a structured `detail` is
   * supplied, the AgentEvent carries the category + hint so downstream
   * consumers (normalizer → runner → platform → frontend) can surface a
   * useful diagnosis instead of the raw upstream string.
   *
   * Reject ordering: `turnReject()` runs FIRST, then the agent-event is
   * emitted. `EventEmitter#emit` calls listeners synchronously; if any
   * listener throws (transport.send on a closed socket, normalizer bug,
   * compaction tracking error, etc.) the throw would otherwise propagate
   * out of failTurn before `turnReject` can run, leaving `await
   * turnPromise` pending forever and silently hanging `driver.prompt(...)`
   * — which in turn prevents the runner's `onAuthError` callback from
   * firing on stale-token 401s. Settling the promise first guarantees the
   * caller transitions even if the listener chain misbehaves; the emit is
   * wrapped in try/catch so a listener failure cannot leak out.
   *
   * Self-heal deferral: when the failure is an {@link AuthError} and the auth
   * self-heal budget is still available (`!authSelfHealUsed`) AND the caller
   * wired an `onAuthError` callback, the downstream `agent-event: error`
   * emission is deferred. The rejected turn promise still travels to
   * {@link prompt}'s catch block where the self-heal runs; if the retry
   * succeeds the user never sees a 401, if the retry fails the second
   * `failTurn` call (now with `authSelfHealUsed === true`) emits the error
   * normally. This stops the historical "401 flashes in the UI even though
   * self-heal worked" misbehaviour where the bridge emitted the error event
   * before the retry decision was known.
   *
   * Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`
   * § "Runner-side handling on 401" (the AI 401 mediation path that
   * exposed this hang).
   */
  private failTurn(err: Error, detail?: import("../types.js").AgentError): void {
    this.disarmTurnStallWatchdog();
    this.disarmErrorResultGrace();
    // Whether a turn actually took this rejection. A deferral is only safe when
    // one did: the deferred event is flushed by `runPrompt`'s catch, and that
    // catch runs only because `turnReject` settled the turn. With no turn in
    // flight — a consumer throw arriving BETWEEN turns on a still-live
    // streaming query — deferring would swallow the failure entirely: no
    // `error`, no `agent_end`, and the next attempt clears the stash.
    const hadPendingTurn = this.turnReject != null;
    if (this.turnReject) {
      this.turnReject(err);
      this.turnResolve = null;
      this.turnReject = null;
    }
    // Mark the turn as completed so the defensive `consumeMessages` finally
    // block (which calls `completeTurn()` regardless of how the iterator
    // exited) does not also emit `agent_end` after we already rejected.
    // Without this, the auth-retry path emits an `agent_end` for the failed
    // attempt AND a second one for the successful retry, doubling the
    // session-end signal downstream. A turn ends EITHER via `completeTurn`
    // (success → emit agent_end) OR via `failTurn` (rejected) — never both.
    this.turnCompleted = true;
    const event: AgentEvent = {
      type: "error",
      error: err.message,
      ...(detail ? { detail } : {}),
    };
    const willSelfHeal =
      err instanceof AuthError && !this.authSelfHealUsed && this.config.onAuthError != null;
    if (hadPendingTurn && willSelfHeal) {
      this.log.info("deferring auth-error event; self-heal will attempt refresh");
      this.deferredTurnError = event;
      return;
    }
    // Same deferral for a usage-limit block: a successful seat failover must be
    // as invisible as a successful 401 self-heal.
    if (hadPendingTurn && this.willFailoverOnLimit()) {
      this.log.info("deferring limit error event; seat failover will be attempted");
      this.deferredTurnError = event;
      return;
    }
    this.emitTurnErrorEvent(event);
  }

  /**
   * Whether a usage-limit block on the current attempt still has a seat
   * failover available. Evaluated identically by {@link failTurn} (to decide
   * whether to withhold the error event) and by {@link handleResultErrorPayload}
   * (to decide whether to reject the turn at all) — one predicate so the two
   * can never disagree and strand a deferred event.
   */
  private willFailoverOnLimit(): boolean {
    return this.limitRejectionSeen && !this.limitFailoverUsed && this.config.onLimitBlocked != null;
  }

  /** Emit one `agent-event: error`, swallowing a throwing listener. */
  private emitTurnErrorEvent(event: AgentEvent): void {
    try {
      this.emit("agent-event", event);
    } catch (emitErr) {
      this.log.warn("agent-event listener threw while surfacing a turn error; suppressing", {
        error: emitErr instanceof Error ? emitErr.message : String(emitErr),
      });
    }
  }

  /**
   * Emit the error {@link failTurn} withheld, exactly once. Called by a
   * recovery path that decided NOT to retry, so every failure produces one
   * downstream error event and a recovered one produces none. `fallback` covers
   * the paths that reject without going through `failTurn`.
   */
  private emitDeferredTurnError(fallback: AgentEvent): void {
    const event = this.deferredTurnError ?? fallback;
    this.deferredTurnError = null;
    this.emitTurnErrorEvent(event);
  }

  // ---------------------------------------------------------------------------
  // Message handling
  // ---------------------------------------------------------------------------

  private handleSdkMessage(msg: SDKMessage): void {
    // SDKRateLimitEvent — claude.ai subscription rate-limit telemetry. Surfaced
    // as a structured warning log so the debug pane and CLI logs show that the
    // upstream limiter is degrading. We do NOT promote `allowed_warning` to a
    // user-visible error; only `rejected` aligns with a turn-blocking failure
    // and that path emits its own `error` event when the SDK returns the
    // result.
    if (this.handleRateLimitEvent(msg)) return;

    if (msg.type === "assistant") {
      this.handleAssistantMessage(msg);
    } else if (msg.type === "stream_event") {
      this.handleStreamEvent(msg);
    } else if (msg.type === "user") {
      this.handleUserMessage(msg);
    } else if (msg.type === "result") {
      this.handleResultMessage(msg);
    }
  }

  /**
   * Surface subscription rate-limit telemetry as a structured warning log, and
   * — on `status: "rejected"` — arm the seat failover and report the rejection
   * to the host for per-seat health attribution.
   *
   * Returns `true` when the message was a rate-limit event (and fully handled),
   * so the caller can stop dispatching.
   *
   * This used to only log. That is what left a session pinned to a parked seat:
   * the rejection was fully handled here and nothing propagated, so the
   * platform's re-resolution — which was ready and working — was reachable only
   * through a token refresh that happened to be due (skaile-ai/platform#2976).
   */
  private handleRateLimitEvent(msg: SDKMessage): boolean {
    const maybeRateLimit = msg as unknown as {
      type?: string;
      rate_limit_info?: {
        status?: string;
        rateLimitType?: string;
        utilization?: number;
        resetsAt?: number;
      };
    };
    if (maybeRateLimit.type === "rate_limit_event" && maybeRateLimit.rate_limit_info) {
      const info = maybeRateLimit.rate_limit_info;
      this.log.warn("upstream rate-limit telemetry", {
        status: info.status,
        type: info.rateLimitType,
        utilization: info.utilization,
        resetsAt: info.resetsAt,
      });
      if (info.status === "rejected") {
        this.limitRejectionSeen = true;
        this.lastLimitInfo = {
          ...(info.rateLimitType ? { limitType: info.rateLimitType } : {}),
          ...(typeof info.resetsAt === "number" ? { resetsAt: info.resetsAt } : {}),
          ...(typeof info.utilization === "number" ? { utilization: info.utilization } : {}),
        };
        this.reportProviderResponse({ status: 429, ...this.lastLimitInfo });
      }
      // Don't emit an agent-event for warning levels — the user only needs to
      // hear about it on `rejected`, which the result handler already covers.
      return true;
    }
    return false;
  }

  /**
   * Report an observed 401 / 429 to the host so the owning AI provider config's
   * live health counters can be bumped (skaile-ai/workspaces#368). Strictly
   * fire-and-forget: a throwing host callback must never fail the turn that
   * produced the observation.
   */
  private reportProviderResponse(observation: Omit<ProviderResponseObservation, "configId">): void {
    if (!this.config.onProviderResponse || this.providerResponseReported) return;
    this.providerResponseReported = true;
    try {
      this.config.onProviderResponse({
        ...observation,
        ...(this.config.aiProviderConfigId ? { configId: this.config.aiProviderConfigId } : {}),
      });
    } catch (err) {
      this.log.warn("onProviderResponse threw; suppressing", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private handleAssistantMessage(msg: SDKAssistantMessage): void {
    this.captureAssistantContext(msg);
    const agentMsg = this.mapAssistantMessage(msg);

    // The SDK renders an upstream 401 as a standalone assistant text message;
    // drop it while a self-heal is still pending so a recovered 401 stays
    // invisible. Rationale: _devlog/notes/2026-06-08-auth-error-text-suppress.md
    if (this.isSuppressibleAuthRender(agentMsg)) {
      this.log.info("suppressing SDK auth-error assistant text; self-heal will retry");
      // Keep the dropped text: it is the only auth evidence this turn has if
      // the failure then arrives as an is_error result with an empty errors[].
      this.stashSuppressedAuthRender(this.assistantTextOf(agentMsg));
      this.prevText = "";
      this.emittedSanitizedLen = 0;
      this.suppressingAuthStream = false;
      return;
    }

    this.emit("agent-event", { type: "message_start", message: agentMsg } satisfies AgentEvent);

    if (agentMsg.toolCalls) {
      for (const tc of agentMsg.toolCalls) {
        this.emit("agent-event", {
          type: "tool_call",
          name: tc.name,
          tool: { name: tc.name },
          input: tc.input,
        });
      }
    }

    this.prevText = "";
    this.emittedSanitizedLen = 0;
    this.suppressingAuthStream = false;
    // Genuine content followed the render, so the turn moved past it: a later
    // detail-free result must not be classified on that stale 401.
    this.suppressedAuthRenderText = null;
    this.emit("agent-event", { type: "message_end", message: agentMsg } satisfies AgentEvent);
  }

  // True when this assistant text is the SDK's render of a 401 about to be self-healed.
  private isSuppressibleAuthRender(msg: AgentMessage): boolean {
    if (msg.toolCalls && msg.toolCalls.length > 0) return false;
    return this.isSuppressibleAuthRenderText(this.assistantTextOf(msg));
  }

  /**
   * True when this text is the CLI's own render of a 401 a pending self-heal
   * will retry. Shared by both suppression sites so they cannot disagree —
   * why that is forced: `_devlog/notes/2026-06-08-auth-error-text-suppress.md`.
   */
  private isSuppressibleAuthRenderText(text: string): boolean {
    if (!this.authSelfHealAvailable()) return false;
    // Runs per stream delta on the accumulated text, so reject cheapest-first:
    // textIsAuthRender's own keyword prefilter drops ordinary prose for one
    // lowercase pass, before the marker scan or the classifier costs anything.
    return this.textIsAuthRender(text) && isCliAuthRenderText(text);
  }

  /** Whether a 401 on this attempt can still be self-healed and retried. */
  private authSelfHealAvailable(): boolean {
    return !this.authSelfHealUsed && this.config.onAuthError != null;
  }

  /**
   * Stash a dropped auth render as evidence for {@link handleResultErrorPayload}.
   *
   * Deliberately stricter than the suppression predicate. Suppression only hides
   * a message, but this text can reach the result path and construct an
   * `AuthError` — which refreshes a credential, bumps that seat's health
   * counters, and replays the turn. `classifyClaudeSdkError` buckets a bare
   * "401" or "unauthorized" as auth, so ordinary prose about authentication
   * qualifies; requiring a marker only the CLI itself emits keeps that prose out.
   */
  private stashSuppressedAuthRender(text: string): void {
    if (!this.authSelfHealAvailable()) return;
    if (!isCliAuthRenderText(text)) return;
    this.suppressedAuthRenderText = text;
  }

  /** Flatten an assistant message's text blocks; non-text blocks contribute nothing. */
  private assistantTextOf(msg: AgentMessage): string {
    return typeof msg.content === "string"
      ? msg.content
      : msg.content
          .filter((b) => b.type === "text" && typeof b.text === "string")
          .map((b) => b.text)
          .join("");
  }

  // Cheap substring pre-filter keeps the full classifier off the hot path of
  // every assistant message / stream delta; only auth-shaped text pays for it.
  private textIsAuthRender(text: string): boolean {
    const lower = text.trim().toLowerCase();
    if (!lower) return false;
    if (!lower.includes("auth") && !lower.includes("401") && !lower.includes("login")) {
      return false;
    }
    return (
      classifyClaudeSdkError({ errors: [text], oauthCredential: this.usingOauthCredential })
        .category === "auth"
    );
  }

  private handleStreamEvent(msg: Extract<SDKMessage, { type: "stream_event" }>): void {
    const event = msg.event;
    if (
      event?.type === "content_block_delta" &&
      event.delta?.type === "text_delta" &&
      event.delta.text
    ) {
      const delta = event.delta.text;
      this.prevText += delta;
      // One predicate with handleAssistantMessage, deliberately: the normalizer
      // skips a message_start whose text already streamed, so a stream dropped
      // on a verdict the assembled message rejects would truncate the answer.
      if (!this.suppressingAuthStream && this.isSuppressibleAuthRenderText(this.prevText)) {
        this.suppressingAuthStream = true;
        this.log.info("suppressing SDK auth-error stream; self-heal will retry");
      }
      if (this.suppressingAuthStream) {
        // Evidence for an is_error result that arrives with no errors[] of its
        // own. Re-stashed per delta so the stash keeps growing with the render
        // rather than freezing at whatever prefix first tripped suppression.
        this.stashSuppressedAuthRender(this.prevText);
        return;
      }
      // Sanitize the *assembled* text (never the raw delta), holding back any
      // trailing partial marker so a leaked tool-call block / fabricated role
      // turn that spans deltas is never half-emitted. The downstream consumer
      // persists `_textDelta`, so the forwarded delta must be the newly-stable
      // *sanitized* suffix — not the raw model delta — or the leak survives in
      // the stored transcript even though `message.content` looks clean.
      const safe = streamingSafeSanitize(this.prevText);
      const sanitizedDelta = safe.slice(this.emittedSanitizedLen);
      this.emittedSanitizedLen = safe.length;
      if (sanitizedDelta.length === 0) return;
      const agentMsg: AgentMessage = {
        role: "assistant",
        content: [{ type: "text", text: safe }],
      };
      this.emit("agent-event", {
        type: "message_update",
        message: agentMsg,
        _textDelta: sanitizedDelta,
      } satisfies AgentEvent as any);
    }
  }

  private handleUserMessage(msg: SDKUserMessage): void {
    const toolResults = this.mapToolResults(msg);
    for (const result of toolResults) {
      this.emit("agent-event", {
        type: "tool_execution_end",
        toolName: result.toolName,
      });
    }
    if (toolResults.length > 0) {
      this.emit("agent-event", { type: "turn_end", toolResults } satisfies AgentEvent);
    }
  }

  private handleResultMessage(msg: Extract<SDKMessage, { type: "result" }>): void {
    this.captureResultUsage(msg);

    // Branch on `is_error` BEFORE `subtype === "success"`. The Claude Agent
    // SDK occasionally emits result messages where `subtype === "success"`
    // AND `is_error === true` AND `errors === []` — observed in production
    // when an upstream `authentication_error` from the Anthropic API made
    // the SDK fail the turn but still tag it as a "successful run" because
    // the agent loop itself (the SDK process) terminated without a process
    // crash. The actual 401 surfaces ~200 ms later as a thrown exception
    // in the for-await iterator. Without this guard, `handleSdkMessage`
    // takes the success branch on subtype, `consumeMessages` calls
    // `completeTurn` (resolving the turn promise), and the subsequent
    // throw lands in the consumer-error catch with `turnReject` already
    // null — the bridge's auth-retry catch in `prompt()` never runs and
    // the user sees a fatal 401. Treating any `is_error: true` result as
    // an error (regardless of subtype) routes through `failTurn` so the
    // turn promise rejects with a structured `AuthError` and the runner's
    // `onAuthError` callback can refresh the credential.
    //
    // Spec: `_devlog/specs/2026-05-07-unified-credential-mediation.md`.
    if (msg.subtype === "success" && !msg.is_error) {
      this.handleSuccessResult(msg);
    } else {
      this.handleErrorResult(msg);
    }
  }

  /**
   * Capture the per-call context size from an assistant message. Its `usage`
   * describes exactly one API request, so `input + cache_creation + cache_read`
   * is the prompt that occupied the window on that call — unlike the result
   * message's usage, which sums every call in the turn. All three are summed
   * rather than `contextTokens()`-style max'd: on a per-call Anthropic usage the
   * fields are disjoint slices of one prompt.
   */
  private captureAssistantContext(msg: SDKAssistantMessage): void {
    // A Task subagent runs its own, far smaller window; letting its fill stand
    // in for the main thread's would under-fire compaction.
    if (msg.parent_tool_use_id) return;
    const model = (msg as any).message?.model;
    if (typeof model === "string" && model) this._lastMainModel = model;
    const usage = (msg as any).message?.usage as
      | {
          input_tokens?: number;
          cache_read_input_tokens?: number;
          cache_creation_input_tokens?: number;
        }
      | undefined;
    if (!usage) return;
    const total =
      (typeof usage.input_tokens === "number" ? usage.input_tokens : 0) +
      (typeof usage.cache_creation_input_tokens === "number"
        ? usage.cache_creation_input_tokens
        : 0) +
      (typeof usage.cache_read_input_tokens === "number" ? usage.cache_read_input_tokens : 0);
    // Synthetic/error assistant messages report all-zero usage; one of those
    // must never wipe a good reading from a real request.
    if (total > 0) this._lastContextTokens = total;
  }

  /**
   * Capture token usage + context-window size from a result message.
   * Anthropic separates fresh prompt input from cached/cache-creation
   * tokens — surface all four to consumers that want a complete picture.
   */
  private captureResultUsage(msg: Extract<SDKMessage, { type: "result" }>): void {
    // Agentic-turn count for the maxTurns recycle guard (shouldRecycleQuery).
    const numTurns = (msg as { num_turns?: number }).num_turns;
    if (typeof numTurns === "number") this.lastNumTurns = numTurns;
    const usage = (msg as any).usage as
      | {
          input_tokens?: number;
          output_tokens?: number;
          cache_read_input_tokens?: number;
          cache_creation_input_tokens?: number;
        }
      | undefined;
    if (usage) {
      const tokens: TokenUsage = {};
      if (typeof usage.input_tokens === "number") tokens.inputTokens = usage.input_tokens;
      if (typeof usage.output_tokens === "number") tokens.outputTokens = usage.output_tokens;
      if (typeof usage.cache_read_input_tokens === "number")
        tokens.cacheReadTokens = usage.cache_read_input_tokens;
      if (typeof usage.cache_creation_input_tokens === "number")
        tokens.cacheCreationTokens = usage.cache_creation_input_tokens;
      this._lastTokens = tokens;
    }
    const modelUsage = (msg as any).modelUsage as Record<string, SdkModelUsage> | undefined;
    if (modelUsage) {
      const main =
        (this._lastMainModel ? modelUsage[this._lastMainModel] : undefined) ??
        Object.values(modelUsage)[0];
      if (main?.contextWindow) this._contextWindow = main.contextWindow;
    }
    // `total_cost_usd` is cumulative for the whole SDK run, not per-turn. A
    // fresh query spawn (or, as a fallback, an observed decrease) means the
    // counter restarted, so the new total IS the delta.
    const reportedCostUsd = (msg as { total_cost_usd?: number }).total_cost_usd;
    this._lastCostReported = typeof reportedCostUsd === "number";
    const totalCostUsd = reportedCostUsd ?? 0;
    const freshCostSession =
      this._querySpawnGeneration !== this._costTrackingGeneration ||
      totalCostUsd < this._lastCumulativeCostUsd;
    this._lastTurnCostUsd = freshCostSession
      ? totalCostUsd
      : totalCostUsd - this._lastCumulativeCostUsd;
    this._lastCumulativeCostUsd = totalCostUsd;
    this._costTrackingGeneration = this._querySpawnGeneration;
    // The CLI feeds `modelUsage` from the same run-wide tracker as
    // `total_cost_usd`, so the turn's share is the same kind of delta.
    if (modelUsage) {
      this._lastModelUsage = diffModelUsage(
        modelUsage,
        freshCostSession ? null : this._lastCumulativeModelUsage,
      );
      this._lastCumulativeModelUsage = modelUsage;
    }
  }

  /** Optional usage fields shared by the success and error `result` events. */
  private turnUsageFields(): {
    modelUsage?: Record<string, ModelTokenUsage>;
    contextTokens?: number;
    contextWindow?: number;
  } {
    return {
      ...(this._lastModelUsage ? { modelUsage: this._lastModelUsage } : {}),
      ...(this._lastContextTokens !== null ? { contextTokens: this._lastContextTokens } : {}),
      ...(this._contextWindow !== null ? { contextWindow: this._contextWindow } : {}),
    };
  }

  /** Emit the message_end (if any) + success result event for a clean turn. */
  private handleSuccessResult(
    msg: Extract<SDKMessage, { type: "result"; subtype: "success" }>,
  ): void {
    // `msg.result` is the SDK's final assembled assistant text and becomes the
    // persisted `finished.summary` — the single durable copy of the answer. It
    // is the block that, in production, swallowed the conversation (leaked
    // tool-call XML + a fabricated `[User]:` follow-up). Sanitize it here.
    const result = msg.result != null ? sanitizeAssistantText(msg.result) : msg.result;
    if (result) {
      const agentMsg: AgentMessage = {
        role: "assistant",
        content: result,
      };
      this.emit("agent-event", { type: "message_end", message: agentMsg } satisfies AgentEvent);
    }
    this.emit("agent-event", {
      type: "result",
      subtype: msg.subtype,
      summary: result ?? "Task completed",
      costUsd: this._lastTurnCostUsd,
      ...(this._lastTokens ? { tokens: this._lastTokens } : {}),
      ...this.turnUsageFields(),
    } as AgentEvent);
  }

  /**
   * Handle the non-clean branch (legacy `SDKResultError`, or a `success`
   * subtype carrying `is_error: true`). Read via the loose shape since the
   * second case omits `errors` on the typed variant. See devlog note
   * 2026-06-08-claude-sdk-error-result-handling.
   */
  private handleErrorResult(msg: Extract<SDKMessage, { type: "result" }>): void {
    const looseMsg = msg as { errors?: string[]; is_error?: boolean; terminal_reason?: string };
    const errors: string[] = looseMsg.errors?.length ? looseMsg.errors : [];
    if (looseMsg.is_error) {
      this.handleResultErrorPayload(msg, errors);
    }
    // The failed turn billed too: carry its usage so the ledger sees it.
    // `costUsd` only when the SDK reported one — 0 would read as a free turn.
    this.emit("agent-event", {
      type: "result",
      subtype: msg.subtype,
      errors,
      ...(this._lastCostReported ? { costUsd: this._lastTurnCostUsd } : {}),
      ...(this._lastTokens ? { tokens: this._lastTokens } : {}),
      ...this.turnUsageFields(),
    } as AgentEvent);
    // Streaming input mode removed the legacy backstop that used to settle
    // non-auth error results: with a string prompt the CLI exited after any
    // result, the generator ended, and consumeMessages' finally ran
    // completeTurn(). The generator staying open is now the point, so an
    // `error_max_turns` / non-auth `error_during_execution` result must end
    // the turn HERE (resolved, matching the legacy semantics) or prompt()
    // hangs until the stall watchdog. Auth/account payloads already settled
    // via failTurn above (turnCompleted is set). The one shape left pending
    // is `is_error` with empty errors[]: its real error arrives as a
    // follow-up iterator throw (see handleResultErrorPayload) and settles the
    // turn via failTurn — a bounded grace timer fails it if no throw comes.
    const awaitingFollowUpThrow = looseMsg.is_error === true && errors.length === 0;
    if (!this.turnCompleted && !awaitingFollowUpThrow) {
      this.completeTurn();
    }
  }

  /**
   * Classify + route an `is_error` result payload: auth-shaped errors throw
   * via `failTurn`; populated non-auth errors surface a normal `error` event;
   * empty errors defer to the consumer-error catch under a bounded grace timer.
   *
   * An empty `errors[]` falls back to the auth render this attempt suppressed:
   * handed nothing, the classifier can only answer `unknown`, stranding a 401
   * the render path already recognised on the same text.
   */
  private handleResultErrorPayload(
    msg: Extract<SDKMessage, { type: "result" }>,
    errors: string[],
  ): void {
    const effectiveErrors =
      errors.length === 0 && this.suppressedAuthRenderText != null
        ? [this.suppressedAuthRenderText]
        : errors;
    // Pass terminalReason/subtype too: `blocking_limit` is authoritatively a
    // quota issue even when errors[] text reads as auth.
    const rawDetail = classifyClaudeSdkError({
      errors: effectiveErrors,
      terminalReason: (msg as { terminal_reason?: string }).terminal_reason,
      subtype: msg.subtype,
      oauthCredential: this.usingOauthCredential,
    });
    // Mirror of handleConsumerError's post-refresh downgrade — a recurring 401
    // can arrive as an is_error result, not only a thrown exception.
    const detail =
      rawDetail.category === "auth" && this.authSelfHealUsed
        ? reclassifyAuthAsAccountUnavailable(rawDetail)
        : rawDetail;
    this.log.error("turn ended with error", {
      subtype: msg.subtype,
      terminalReason: (msg as { terminal_reason?: string }).terminal_reason,
      errorsCount: errors.length,
      fromSuppressedAuthRender: effectiveErrors !== errors,
      category: detail.category,
      retryable: detail.retryable,
    });
    if (detail.category === "auth") {
      this.reportProviderResponse({ status: 401 });
    } else if (isSeatCapacityFailure(detail, msg.subtype)) {
      this.limitRejectionSeen = true;
      this.reportProviderResponse({ status: 429, ...(this.lastLimitInfo ?? {}) });
    }
    const errorText = effectiveErrors.join("; ") || `Agent stopped: ${msg.subtype}`;
    // auth/account → thrown via failTurn (runner discriminates by instance);
    // populated errors → normal error event; empty errors → bounded defer to
    // the consumer-error catch. Note 2026-06-08-claude-sdk-error-result-handling.
    if (detail.category === "auth") {
      this.failTurn(new AuthError({ ...detail, message: errorText }), detail);
    } else if (detail.category === "account") {
      this.failTurn(new AccountUnavailableError({ ...detail, message: errorText }), detail);
    } else if (this.willFailoverOnLimit()) {
      // A usage-limit block with a seat failover still available. Reject the
      // turn instead of emitting the error and resolving, so `runPrompt`'s
      // catch can switch seats and replay — the failover is unreachable from a
      // resolved turn. `failTurn` withholds the error event, so a successful
      // switch stays invisible exactly as a successful 401 self-heal does.
      this.limitTurnRejectedForFailover = true;
      this.failTurn(new Error(errorText), detail);
    } else if (errors.length > 0) {
      // Real error info present — surface it normally. The result's OWN errors,
      // never `effectiveErrors`: this branch settles nothing, so a substituted
      // one would hang. Note 2026-06-08-claude-sdk-error-result-handling.
      this.emit("agent-event", {
        type: "error",
        error: errorText,
        detail,
      } satisfies AgentEvent);
    } else {
      // is_error + no error text anywhere → defer to the consumer-error catch,
      // bounded: the promised follow-up throw is not guaranteed to come.
      this.armErrorResultGrace(detail, {
        subtype: msg.subtype,
        terminalReason: (msg as { terminal_reason?: string }).terminal_reason,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Build an SDKUserMessage for the persistent prompt stream. */
  private buildUserMessage(text: string): SDKUserMessage {
    return {
      type: "user",
      message: {
        role: "user",
        content: text,
      },
      parent_tool_use_id: null,
      session_id: this.sessionId,
    };
  }

  private mapAssistantMessage(msg: SDKAssistantMessage): AgentMessage {
    const contentBlocks: ContentBlock[] = [];
    const toolCalls: ToolCall[] = [];
    const content = msg.message.content;

    if (Array.isArray(content)) {
      for (const block of content) {
        if (block.type === "text") {
          // Strip any leaked tool-call XML / fabricated `[User]:` follow-up
          // turn the model echoed into prose before it is persisted.
          contentBlocks.push({ type: "text", text: sanitizeAssistantText(block.text) });
        } else if (block.type === "tool_use") {
          contentBlocks.push({
            type: "tool_use",
            id: block.id,
            name: block.name,
            input: block.input,
          });
          toolCalls.push({ id: block.id, name: block.name, input: block.input });
          this.toolIdToName.set(block.id, block.name);
        } else if (block.type === "thinking") {
          contentBlocks.push({ type: "thinking", text: block.thinking });
        }
      }
    }

    return {
      role: "assistant",
      content: contentBlocks,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
    };
  }

  private mapToolResults(msg: SDKUserMessage): AgentMessage[] {
    const results: AgentMessage[] = [];
    const content = msg.message.content;

    if (Array.isArray(content)) {
      for (const block of content) {
        if (block.type === "tool_result") {
          results.push({
            role: "tool",
            content:
              typeof block.content === "string" ? block.content : JSON.stringify(block.content),
            toolName: this.toolIdToName.get(block.tool_use_id) ?? block.tool_use_id,
            isError: block.is_error,
          });
        }
      }
    }

    return results;
  }

  /**
   * Requests the current SDK query to stop processing via `query.interrupt()` and
   * signals the underlying `AbortController`. The driver remains usable after abort.
   *
   * Also SIGKILLs every OS descendant of the CLI subprocess (e.g. a running
   * Bash command) on either path the turn ends — see
   * {@link snapshotDescendantPids}'s doc for why that's needed.
   *
   * @remarks Best-effort: if `interrupt()` throws (e.g. query already ended) the
   *   error is silently swallowed and the abort signal is still sent. A query
   *   whose generator refuses to unwind within {@link ABORT_GRACE_MS} is torn
   *   down outright — see {@link hardResetQuery}.
   */
  public async abort(): Promise<void> {
    // Cancel any question the turn is paused on so it unwinds with a clean
    // tool_result rather than dangling when the turn is interrupted.
    this.denyPendingQuestion("turn aborted before the question was answered");
    // Between turns there is nothing to stop. With a resident streaming query,
    // an idle abort would kill the healthy CLI and SIGKILL its background
    // tasks (dev servers) — legacy idle abort was a no-op because the CLI had
    // already exited after the turn's result. Keep that no-op contract; an
    // abort racing a just-completed turn leaves the session fully usable.
    if (!this.running && this.query) return;
    const abortedQuery = this.query;
    const abortedController = this.abortController;
    // Snapshot BEFORE interrupt()/abort() touch anything — once the CLI exits
    // (which either of those can trigger), Linux reparents its children
    // immediately, and a tree walk from the now-dead CLI pid finds nothing.
    const descendants = this.snapshotDescendantPids();
    if (abortedQuery) {
      try {
        await abortedQuery.interrupt();
      } catch {
        /* best effort */
      }
    }
    abortedController?.abort();

    // consumeMessages()'s finally nulls `query` once the generator unwinds.
    // A wedged SDK process never unwinds, so force the teardown — otherwise
    // the next prompt streamInput()s into a dead query and hangs too.
    const deadline = Date.now() + ABORT_GRACE_MS;
    while (abortedQuery && this.query === abortedQuery && Date.now() < deadline) {
      await new Promise((resolve) => {
        setTimeout(resolve, 100).unref?.();
      });
    }
    if (abortedQuery && this.query === abortedQuery) {
      this.log.warn("query did not unwind after abort; forcing teardown");
      this.hardResetQuery();
      // The healthy path settles the turn from consumeMessages()'s finally. The
      // forced path has no consumer left, so settle it here — otherwise the
      // in-flight prompt() stays pending forever and every awaiter of the turn
      // tail (driver swaps, the runner's endTurn) hangs with it.
      this.completeTurn();
    }
    // Sweep in both branches — a "clean" unwind only proves the SDK's
    // connection to the CLI closed, not that its child process did too.
    this.killPids(descendants);
  }

  /** `true` while the driver is processing a `prompt()` call. */
  get isRunning(): boolean {
    return this.running;
  }

  /**
   * Closes the active SDK query and resets all session state.
   *
   * The driver instance must not be reused after `kill()`. Session IDs are cleared,
   * so a new `ClaudeSdkDriver` will start a fresh session.
   *
   * Also SIGKILLs every OS descendant of the CLI subprocess (e.g. a running
   * Bash command) — see {@link snapshotDescendantPids}'s doc for why that
   * needs to be snapshotted before `query.close()` runs, not after.
   */
  public kill(): void {
    this.denyPendingQuestion("session terminated before the question was answered");
    const descendants = this.snapshotDescendantPids();
    this.endInputStream();
    if (this.query) {
      try {
        this.query.close();
      } catch {
        /* best effort */
      }
      this.query = null;
    }
    this.abortController?.abort();
    this.killPids(descendants);
    this.running = false;
    this.sessionId = "";
    this.hasSession = false;
  }

  override async listModels(): Promise<ModelEntry[]> {
    // Bedrock / Vertex / Azure address models through their own control planes
    // with their own credentials — an Anthropic `/v1/models` call is the wrong
    // endpoint (and a key leak) there, so those still report nothing.
    // `gateway` is different: it speaks Anthropic's wire protocol at a
    // configured base URL, so discovery works as long as it is aimed there
    // rather than at api.anthropic.com. Aiming it at the public API was the
    // reason a gateway session reported an empty catalog, which callers that
    // read "empty" as "unknown" then degraded into "accept any model".
    // resolveCloud() keeps this consistent with startQuery (unknown → default).
    const cloud = this.resolveCloud();
    if (cloud !== "default" && cloud !== "gateway") return [];

    const baseUrl =
      cloud === "gateway"
        ? this.config.cloudConfig?.baseUrl ||
          this.config.env?.ANTHROPIC_BASE_URL ||
          process.env.ANTHROPIC_BASE_URL
        : undefined;
    // A gateway session carries its credential in `cloudSecrets` (auth token
    // preferred, mirroring buildClaudeCloudEnv's carrier precedence).
    const apiKey =
      (cloud === "gateway"
        ? this.config.cloudSecrets?.ANTHROPIC_AUTH_TOKEN ||
          this.config.cloudSecrets?.ANTHROPIC_API_KEY
        : undefined) ||
      this.config.apiKeys?.anthropic ||
      this.config.env?.ANTHROPIC_API_KEY ||
      process.env.ANTHROPIC_API_KEY;
    if (!apiKey) return [];
    if (cloud === "gateway" && !baseUrl) return [];

    const result = await fetchProviderModels("anthropic", apiKey, { baseUrl });
    return result.ok ? (result.models as ModelEntry[]) : [];
  }

  override getTokenUsage(): TokenUsage | null {
    return this._lastTokens;
  }

  override getContextTokens(): number | null {
    return this._lastContextTokens;
  }

  override getContextWindow(): number | null {
    return this._contextWindow;
  }

  async resetSession(): Promise<void> {
    this.denyPendingQuestion("session reset before the question was answered");
    // Same descendant-sweep bracket as abort()/kill() — see snapshotDescendantPids's doc.
    const descendants = this.snapshotDescendantPids();
    this.endInputStream();
    if (this.query) {
      try {
        this.query.close();
      } catch {
        /* ignore */
      }
      this.query = null;
    }
    this.abortController?.abort();
    this.killPids(descendants);
    this.sessionId = "";
    this.hasSession = false;
    this._lastTokens = null;
    this._lastContextTokens = null;
    this._contextWindow = null;
    this._lastModelUsage = null;
    this._lastCumulativeModelUsage = null;
    this._lastMainModel = null;
    this._lastCumulativeCostUsd = 0;
    this._lastTurnCostUsd = 0;
    this._lastCostReported = false;
    this._costTrackingGeneration = -1;
  }

  /**
   * Build allowedTools / disallowedTools options from AgentConfig.tools.
   *
   * When an allowlist is active, automatically prepend a wildcard entry for
   * every configured MCP server (e.g. `mcp__skaile-connectors__*`) so that
   * connector tools are never silently blocked by agent.yaml restrictions.
   */
  private buildToolRestrictions(): Pick<SdkOptions, "allowedTools" | "disallowedTools"> {
    const mcpWildcards = this.config.mcpServers
      ? Object.keys(this.config.mcpServers).map((name) => `mcp__${name}__*`)
      : [];
    if (this.config.capabilities) {
      mcpWildcards.push("mcp__skaile-capabilities__*");
    }

    const result: Pick<SdkOptions, "allowedTools" | "disallowedTools"> = {};

    // Only set allowedTools if the manifest explicitly declared an allow list.
    // MCP wildcards are appended to ensure connector tools aren't blocked by
    // the allowlist, but they should NOT create an allowlist on their own —
    // that would block all non-MCP tools (Read, Write, Bash, etc.).
    const allowed = this.config.tools?.allowed;
    if (allowed?.length) {
      result.allowedTools = [...allowed, ...mcpWildcards];
    }

    if (this.config.tools?.denied?.length) {
      result.disallowedTools = this.config.tools.denied;
    }

    return result;
  }

  /**
   * Map the simple string thinking config to SDK ThinkingConfig objects.
   * This keeps the platform-layer SDK-agnostic while the bridge handles the mapping.
   */
  private mapThinkingConfig(
    thinking: "adaptive" | "enabled" | "disabled",
  ): { type: "adaptive" } | { type: "enabled" } | { type: "disabled" } {
    switch (thinking) {
      case "adaptive":
        return { type: "adaptive" };
      case "enabled":
        return { type: "enabled" };
      case "disabled":
        return { type: "disabled" };
    }
  }

  /**
   * Find the Claude Code CLI executable.
   *
   * Resolution order:
   * 1. CLAUDE_CODE_PATH env var (set in Docker containers where cli.js is
   *    extracted from the SDK during build — bun-compiled binaries can't
   *    access the SDK's bundled cli.js via $bunfs)
   * 2. `which claude` on PATH (dev machines with claude installed globally)
   * 3. undefined — let the SDK resolve its own bundled cli.js (works when
   *    not running inside a bun-compiled binary)
   */
  private findClaudeBinary(): string | undefined {
    // Memoized process-wide — resolution is invariant for the process lifetime,
    // so `spawnSync("which", ...)` runs at most once instead of every startQuery().
    if (claudeBinaryResolved) return claudeBinaryCache;

    const envPath = process.env.CLAUDE_CODE_PATH;
    if (envPath && existsSync(envPath)) {
      claudeBinaryCache = envPath;
    } else {
      const result = spawnSync("which", ["claude"], { encoding: "utf-8" });
      claudeBinaryCache = result.stdout?.trim() || undefined;
    }
    claudeBinaryResolved = true;
    return claudeBinaryCache;
  }
}

/**
 * `DriverTarget` wrapper for the claude-sdk driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`. The Claude Agent SDK is lazy-imported
 * inside `start()` — this target export never pulls it at module top.
 */
export const claudeSdkDriverTarget: DriverTarget<AgentConfig> = {
  id: "claude-sdk",
  displayName: BUILTIN_DRIVER_CATALOG["claude-sdk"]?.name ?? "Claude Agent SDK",
  apiVersion: 1,
  // Drivers take AgentConfig; passthrough — full schema validation is out of scope.
  // `zStatic.custom` keeps the static-import discipline (see zNS unwrap above).
  configSchema: zStatic.custom((): boolean => true),
  create: async (config: AgentConfig, _ctx) => new ClaudeSdkDriver(config),
  listModels: async (apiKeys: Record<string, string>) =>
    new ClaudeSdkDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
